import * as THREE from 'three/webgpu';
import { VisFrameInfo, VisObjectFrameInfo } from './animationController';
import i18next from 'i18next';
import {
    initThreeContext,
    loadUnifiedModel,
    exportVideoWithRecordingCamera,
    RecordingEnhancementOptions,
    RecordingCamera,
    VideoQuality,
    VideoCodec,
    ITimelineController,
    GizmoManager,
    GizmoMode,
    GizmoEvent,
    GaussianModel,
    EnvMapHelper,
} from 'visionary-core';
import { cloneFile, getI18nText } from '@/utils/commonFunc';
import { globalConfig, globalManager, globalTools, modelDialogTools } from '@/utils/globalConfig';
import { VideoExportOptions } from '@/utils/sceneUtils';
import { mediaController } from '@/visionaryControllers/mediaController';
import { bootstrapUI } from '@/uiControls/base/bootstrapui';

// import { Sky } from 'three/examples/jsm/objects/Sky.js';
import { modelUtils } from '@/utils/modelUtils';
import { isPlyHeatmapFile } from '@/utils/fileUtils';
import { PlyModel } from '@/uiControls/panelControls/heatmap-panel-tools';
import { computeQueryWeights, computeGramMatrix } from "@/utils/heatmap.func";
export class VisionaryObject {
    id: string;
    name: string;
    object: any;
    file: File;
    url: string;
    type: string;
    isLoop: boolean;
    isPlyHeatmap: boolean;
    // 高斯模型需要，高斯参数，是否动画
    constructor(id: string, name: string, type: string, object: any, file: File, url: string) {
        this.id = id;
        this.name = name;
        this.object = object;
        this.file = file;
        this.url = url;
        this.type = type;
        this.isLoop = false;
        this.isPlyHeatmap = false;
    }

    public setIsPlyHeatmap(isPlyHeatmap: boolean) {
        this.isPlyHeatmap = isPlyHeatmap;
    }
}
export class VisionaryScene {
    private id: string;
    private domid: string;
    private isLoopAnimation: boolean = true; // 是否循环刷新，当清空场景时，设置为false，清空后设置为true
    private isDestroyed: boolean = false;
    private scene: THREE.Scene | null;
    private camera: THREE.PerspectiveCamera | null;
    private renderer: THREE.WebGPURenderer | null;

    private grid: THREE.Group | null;
    private gridBase: THREE.GridHelper | null;
    private currentGridDensity: number = 1;
    private objects: VisionaryObject[];
    private animationId: number | null = null;
    private objectChangeCallback: (() => void) | null = null;
    private sceneResetCallback: (() => void) | null = null;
    private gaussianThreeJSRenderer: any = null;
    private startTime: number;
    private initPromise: Promise<void>;
    private preRenderCallbacks: Array<() => void | Promise<void>> = [];
    private postRenderCallbacks: Array<() => void | Promise<void>> = [];

    private recordingPreviewCallback: (() => void | Promise<void>) | null = null;
    private resizeHandler: () => void;
    private modelSelectChangeHandler: (event: Event) => void;
    private gizmoManager: GizmoManager | null = null;
    private gizmoInitPromise: Promise<void> | null = null;
    private currentGizmoMode: GizmoMode | 'normal' = 'normal';

    private currentSelectedObjectId: string | null = null;
    private gizmoReadyCallbacks: Array<(gizmo: GizmoManager) => void> = [];
    private gizmoModePanel: HTMLElement | null = null;
    private gizmoOverlayScene: THREE.Scene | null = null;
    private isTransforming: boolean = false;
    private editorHelperVisibilityCache: Array<{ object: THREE.Object3D, visible: boolean }> = [];
    // HDR 天空盒缓存与开关
    private skyboxUrl: string = globalConfig.lightSettings.skyboxSettings.url;
    private hdrEnvMap: THREE.Texture | null = null;
    private hdrBackground: THREE.Texture | THREE.Color | null = null;
    private skyboxEnabled: boolean = true;

    // Heatmap UI + state (language similarity overlay)

    private readonly defaultBackgroundColor: number = 0x2d2d2d; // Unity 风格灰

    private raycaster = new THREE.Raycaster();
    private mouse = new THREE.Vector2();
    private selectables: THREE.Object3D[] = [];
    private gaussianProxyMap = new Map<GaussianModel, THREE.Object3D>();
    private isPickingPivot: boolean = true;
    private syncRecordingCamera: RecordingCamera | null = null;
    private activeRecordingPreviewCamera: RecordingCamera | null = null;

    private isExportingVideo: boolean = false;

    // 开发用 HUD：JS 渲染循环 FPS（1 秒窗口）。未注册回调时不产生副作用
    private onFPSUpdate: ((fps: number | null) => void) | null = null;
    private fpsFrames: number = 0;
    private fpsWindowStart: number = 0;
    private fpsIdleNotified: boolean = false;

    private ambientLight: THREE.AmbientLight | null = null;
    private directionalLight: THREE.DirectionalLight | null = null;
    private skybox: THREE.CubeTexture | null = null;

    constructor(id: string) {
        this.id = id;
        this.domid = id;
        this.scene = null;
        this.camera = null;
        this.renderer = null;
        this.grid = null;
        this.gridBase = null;
        this.objects = []; // 存储创建的对象
        this.startTime = Date.now();

        this.gizmoOverlayScene = null;

        // 保存 resize 处理器引用以便后续移除
        this.resizeHandler = () => this.onWindowResize();
        this.modelSelectChangeHandler = (event: Event) => this.onModelSelectChange(event);

        this.initPromise = this.initAsync();
    }

    async initAsync() {
        modelDialogTools.showLoadingInfoModal(getI18nText('loadingMessage.initializingScene'), true);
        await this.init();
        const currentSelected = globalManager.getCurrentSelected();
        if (currentSelected && currentSelected.sceneid != this.id) { // 如果当前选中对象的场景id不等于当前场景id且当前选中对象为录制相机，则同步录制相机-打开预览
            const selectedObject = globalManager.getCurrentObject();
            if (selectedObject && selectedObject.type === 'recordingCamera') {
                this.syncRecordingCameraByObject(selectedObject);
            }
        }
        modelDialogTools.showLoadingInfoModal(getI18nText('loadingMessage.scenePanelInitComplete'));
        this.animate();
    }

    /**
     * 等待场景初始化完成
     */
    public async waitForInit(): Promise<void> {
        return this.initPromise;
    }

    async init() {
        // 创建WebGPU渲染器
        const container = document.getElementById(this.domid);
        if (!container) {
            throw new Error(`Container element with id '${this.domid}' not found`);
        }
        console.log("找到渲染器并进行初始化")
        // 创建场景
        this.scene = new THREE.Scene();
        this.scene.background = new THREE.Color(this.defaultBackgroundColor);

        this.gizmoOverlayScene = new THREE.Scene();

        // 创建相机
        this.camera = new THREE.PerspectiveCamera(
            globalConfig.cameraParams.fov,
            container.clientWidth / container.clientHeight,
            globalConfig.cameraParams.nearPlane,
            globalConfig.cameraParams.farPlane
        );
        this.camera.position.set(globalConfig.cameraParams.position.x, globalConfig.cameraParams.position.y, globalConfig.cameraParams.position.z);

        // 如果提供了initThreeContext，使用它来初始化渲染器
        if (initThreeContext) {
            // 创建一个canvas元素供initThreeContext使用
            const canvas = document.createElement('canvas');
            canvas.width = container.clientWidth;
            canvas.height = container.clientHeight;
            container.appendChild(canvas);

            // initThreeContext会使用这个canvas创建WebGPURenderer
            this.renderer = await initThreeContext(canvas);
            if (!this.renderer) {
                throw new Error("Failed to initialize the three context!");
            }
        } else {
            // 回退到原始方法
            this.renderer = new THREE.WebGPURenderer({ antialias: true });
            // 确保渲染器完全初始化（构建后环境必需）
            await this.renderer.init();
            this.renderer.setSize(container.clientWidth, container.clientHeight);
            // WebGPURenderer可能没有shadowMap属性，需要检查
            if ('shadowMap' in this.renderer) {
                (this.renderer as any).shadowMap.enabled = true;
                (this.renderer as any).shadowMap.type = THREE.PCFSoftShadowMap;
            }
            container.appendChild(this.renderer.domElement);
        }

        // 添加光源（等待环境贴图加载完成）
        await this.setupLighting();
        // this.setupShadow(true);

        // 创建网格（默认显示）
        this.createGrid();

        // 监听窗口大小变化
        window.addEventListener('resize', this.resizeHandler);
        this.renderer?.domElement.addEventListener('dblclick', this.handleCanvasClick);

        window.addEventListener('event.modelSelectChange', this.modelSelectChangeHandler);
        await this.initializeGizmoManager();

        // Heatmap (language similarity) inputs are now loaded via the Heatmap panel (on-demand).
    }

    private async initializeGizmoManager(): Promise<void> {
        if (this.gizmoManager || this.gizmoInitPromise) {
            return this.gizmoInitPromise ?? Promise.resolve();
        }

        if (!this.scene || !this.camera || !this.renderer) {
            return Promise.resolve();
        }
        this.gizmoInitPromise = (async () => {
            try {
                this.gizmoManager = new GizmoManager(this.scene as THREE.Scene, this.camera as THREE.Camera, this.renderer as THREE.WebGPURenderer, {
                    mode: 'translate',
                    space: 'local',
                    size: 1,
                    showHelper: true,
                    showPivotHelper: false, // 禁用场景中心的彩色坐标系标识
                    colors: {
                        x: '#ff0000',
                        y: '#00ff00',
                        z: '#0000ff'
                    }
                }, this.gizmoOverlayScene as THREE.Scene);
                if (this.currentGizmoMode != 'normal') {
                    this.gizmoManager.setMode(this.currentGizmoMode);
                    this.gizmoManager.setEnabled(true);
                } else {
                    this.gizmoManager.setEnabled(false);
                    this.gizmoManager.setTarget(null);
                }

                this.addPostRenderCallback(() => {
                    this.gizmoManager?.update();
                });

                if (this.currentSelectedObjectId && this.gizmoManager.getEnabled()) { // 如果当前选中对象且gizmo启用，则设置目标对象
                    const selectedObj = this.objects.find(obj => obj.id === this.currentSelectedObjectId);
                    if (selectedObj) {
                        this.gizmoManager.setTarget(selectedObj.object as THREE.Object3D);
                    }
                }
                // 创建gizmo模式控制面板
                this.createGizmoModePanel();

                for (const callback of this.gizmoReadyCallbacks) {
                    try {
                        callback(this.gizmoManager);
                    } catch (error) {
                        console.error('[VisionaryScene] Gizmo ready callback error:', error);
                    }
                }
                // this.gizmoReadyCallbacks = [];

                // Gizmo 拖拽状态，拖拽时不响应拾取
                this.gizmoManager.setCallbacks({
                    onChange: (event: GizmoEvent) => {
                        window.dispatchEvent(new CustomEvent('event.gizmoObjectChange', { detail: event }));

                        // ✅ 同步 RecordingCamera 的相机位置（如果当前选中的是 RecordingCamera）
                        if (this.currentSelectedObjectId) {
                            const obj = this.objects.find(obj => obj.id === this.currentSelectedObjectId);
                            this.syncRecordingCameraIfNeeded(obj, 'gizmoObjectChange');
                        }
                    },
                    onChangeStart: () => { this.isTransforming = true; },
                    onChangeEnd: () => {
                        this.isTransforming = false;

                        // ✅ 变化结束时也同步一次，确保最终状态正确
                        if (this.currentSelectedObjectId) {
                            const obj = this.objects.find(obj => obj.id === this.currentSelectedObjectId);
                            this.syncRecordingCameraIfNeeded(obj, 'gizmoObjectChangeEnd');
                        }
                    }
                });
            } catch (error) {
                console.warn('[VisionaryScene] 初始化 GizmoManager 失败:', error);
            }
        })();

        try {
            await this.gizmoInitPromise;
        } finally {
            this.gizmoInitPromise = null;
        }
    }

    // 隐藏编辑器辅助器
    private hideEditorHelpers(): void {
        if (!this.scene) return;
        this.editorHelperVisibilityCache.length = 0;
        this.scene.traverse((object: THREE.Object3D) => {
            if (object.userData && (object.userData.__visionaryEditorHelper || object.userData.type === 'recordingCamera')) { // 隐藏编辑器辅助器和录制相机
                this.editorHelperVisibilityCache.push({ object, visible: object.visible });
                object.visible = false;
            }
        });
    }

    private restoreEditorHelpers(): void {
        for (const entry of this.editorHelperVisibilityCache) {
            entry.object.visible = entry.visible;
        }
        this.editorHelperVisibilityCache.length = 0;
    }

    // 环境光和光源设置
    async setupLighting() {
        if (!this.scene) return;

        // 环境光
        // const ambientLight = new THREE.AmbientLight(0x404040, 0.6);
        const ambientLight = new THREE.AmbientLight(0x404040, 1);
        this.scene.add(ambientLight);

        // 主平行光源（从上方照射）
        const mainDirectionalLight = new THREE.DirectionalLight(0xffffff, 0.9);
        mainDirectionalLight.position.set(10, 10, 5);
        mainDirectionalLight.castShadow = true;
        mainDirectionalLight.shadow.mapSize.width = 2048;
        mainDirectionalLight.shadow.mapSize.height = 2048;
        this.scene.add(mainDirectionalLight);

        // 辅助平行光源（从另一侧补充照明）
        const fillDirectionalLight = new THREE.DirectionalLight(0xffffff, 0.3);
        fillDirectionalLight.position.set(-5, 5, -5);
        this.scene.add(fillDirectionalLight);

        // 等待环境贴图加载完成
        await this.setupHDRSkybox();

        // 环境贴图加载完成后，更新所有已存在的材质
        this.updateMaterialsEnvironment();
    }

    /**
     * 设置场景阴影（开启/关闭）
     * @param enabled 是否启用阴影
     * @param shadowMapType 阴影贴图类型，默认为 PCFSoftShadowMap。可选值：BasicShadowMap、PCFShadowMap、PCFSoftShadowMap、VSMShadowMap
     */
    setupShadow(enabled: boolean, shadowMapType: number = THREE.PCFSoftShadowMap): void {
        if (!this.renderer || !this.scene) {
            console.warn('[VisionaryScene] 无法设置阴影：renderer 或 scene 未初始化');
            return;
        }

        // 设置渲染器的阴影贴图
        if ('shadowMap' in this.renderer) {
            const shadowMap = (this.renderer as any).shadowMap;
            shadowMap.enabled = enabled;
            shadowMap.type = enabled ? shadowMapType : THREE.BasicShadowMap;
            console.log(`[VisionaryScene] 渲染器阴影贴图已${enabled ? '启用' : '禁用'}，类型: ${shadowMap.type}`);
        } else {
            console.warn('[VisionaryScene] 警告：WebGPURenderer 可能不支持 shadowMap 属性。请检查 Three.js 版本是否支持 WebGPU 阴影。');
            const r: any = this.renderer as any;
            console.log('[VisionaryScene] Renderer 类型:', r?.constructor?.name ?? typeof r);
            console.log('[VisionaryScene] Renderer 属性:', Object.keys(r ?? {}));
        }

        // 统计信息
        let lightCount = 0;
        let meshCount = 0;
        let basicMaterialCount = 0;

        // 遍历场景中的所有光源，设置支持阴影投射的光源的 castShadow
        this.scene.traverse((object: THREE.Object3D) => {
            // 检查是否为支持阴影的光源类型
            if (object instanceof THREE.DirectionalLight ||
                object instanceof THREE.SpotLight ||
                object instanceof THREE.PointLight) {
                object.castShadow = enabled;
                lightCount++;

                // 当启用阴影时，优化阴影贴图参数
                if (enabled && object.shadow) {
                    // 确保阴影贴图有合理的尺寸（如果还未设置）
                    if (object.shadow.mapSize.width === 0 || object.shadow.mapSize.height === 0) {
                        object.shadow.mapSize.width = 2048;
                        object.shadow.mapSize.height = 2048;
                    }

                    // 对于 DirectionalLight，设置合理的阴影相机范围
                    if (object instanceof THREE.DirectionalLight) {
                        const shadowCamera = object.shadow.camera as THREE.OrthographicCamera;
                        if (shadowCamera) {
                            // 设置一个合理的阴影相机范围（可以根据场景调整）
                            const size = 50;
                            shadowCamera.left = -size;
                            shadowCamera.right = size;
                            shadowCamera.top = size;
                            shadowCamera.bottom = -size;
                            shadowCamera.near = 0.1;
                            shadowCamera.far = 500;

                            // 确保阴影相机朝向正确（跟随光源方向）
                            const lightDirection = new THREE.Vector3();
                            object.getWorldDirection(lightDirection);
                            const center = new THREE.Vector3(0, 0, 0); // 场景中心
                            shadowCamera.position.copy(object.position);
                            shadowCamera.lookAt(center);
                            shadowCamera.updateProjectionMatrix();
                        }
                    }

                    // 对于 SpotLight 和 PointLight，设置阴影相机近远平面
                    if (object instanceof THREE.SpotLight || object instanceof THREE.PointLight) {
                        const shadowCamera = object.shadow.camera;
                        if (shadowCamera) {
                            shadowCamera.near = 0.1;
                            shadowCamera.far = 500;
                            shadowCamera.updateProjectionMatrix();
                        }
                    }

                    // 设置阴影偏移以避免阴影失真
                    object.shadow.bias = -0.0001;
                    object.shadow.normalBias = 0.02;
                }
            }

            // 遍历所有 Mesh，设置 castShadow 和 receiveShadow
            if (object instanceof THREE.Mesh) {
                object.castShadow = enabled;
                object.receiveShadow = enabled;
                meshCount++;

                // 确保材质支持阴影（如果是 MeshStandardMaterial 或 MeshPhysicalMaterial）
                if (object.material) {
                    const materials = Array.isArray(object.material) ? object.material : [object.material];
                    materials.forEach((material) => {
                        // 如果材质不支持阴影，尝试替换为支持阴影的材质
                        if (material instanceof THREE.MeshBasicMaterial) {
                            // MeshBasicMaterial 不支持阴影，需要替换
                            basicMaterialCount++;
                            if (enabled) {
                                console.warn(`[VisionaryScene] Mesh ${object.name || object.uuid} 使用 MeshBasicMaterial，不支持阴影，建议使用 MeshStandardMaterial`);
                            }
                        } else if (material instanceof THREE.MeshStandardMaterial ||
                            material instanceof THREE.MeshPhysicalMaterial ||
                            material instanceof THREE.MeshLambertMaterial ||
                            material instanceof THREE.MeshPhongMaterial) {
                            // 这些材质默认支持阴影，无需额外设置
                        }
                    });
                }
            }
        });
    }

    private disposeSkyboxTextures(): void {
        if (this.hdrEnvMap) {
            this.hdrEnvMap.dispose();
            this.hdrEnvMap = null;
        }
        if (this.hdrBackground instanceof THREE.Texture) {
            this.hdrBackground.dispose();
            this.hdrBackground = null;
        } else {
            this.hdrBackground = null;
        }
    }

    /** PMREM 会创建比输入更大的中间纹理，需预留余量 */
    private getSafeEquirectangularMaxWidth(): number {
        const device = (this.renderer as any)?.backend?.device as GPUDevice | undefined;
        const maxDim = device?.limits?.maxTextureDimension2D ?? 8192;
        return Math.min(Math.floor(maxDim / 2), 4096);
    }

    private downscaleEquirectangularTexture(texture: THREE.Texture): THREE.Texture {
        const image = texture.image as { width?: number; height?: number } | null;
        if (!image?.width || !image?.height) {
            return texture;
        }

        const maxWidth = this.getSafeEquirectangularMaxWidth();
        if (image.width <= maxWidth) {
            return texture;
        }

        const targetWidth = maxWidth;
        const targetHeight = Math.max(1, Math.round(targetWidth / 2));

        const canvas = document.createElement('canvas');
        canvas.width = targetWidth;
        canvas.height = targetHeight;
        const ctx = canvas.getContext('2d');
        if (!ctx) {
            return texture;
        }
        ctx.drawImage(image as CanvasImageSource, 0, 0, targetWidth, targetHeight);

        const resized = new THREE.CanvasTexture(canvas);
        resized.mapping = texture.mapping;
        resized.colorSpace = texture.colorSpace;
        resized.needsUpdate = true;
        texture.dispose();
        return resized;
    }

    private async setupHDRSkybox(url?: string): Promise<void> {
        if (!this.scene || !this.renderer) {
            return;
        }
        const targetUrl = url ?? this.skyboxUrl;
        try {
            // 加载 HDR / EXR 环境贴图纹理
            const texture = this.downscaleEquirectangularTexture(
                await EnvMapHelper.loadHDRTexture(targetUrl)
            );

            // 在 WebGPU 渲染器中，尝试使用 PMREMGenerator 处理环境贴图
            const envMap = await EnvMapHelper.createPMREMEnvironmentMap(this.renderer, texture) || texture;

            // 设置场景环境贴图和背景
            EnvMapHelper.setupSceneEnvironment(this.scene, envMap, texture);
            this.hdrEnvMap = envMap;
            this.hdrBackground = texture;

            // 设置色调映射
            EnvMapHelper.setupRendererToneMapping(this.renderer);

            // 更新渲染器环境贴图
            EnvMapHelper.updateRendererEnvironment(this.renderer, this.scene);

            console.log('[VisionaryScene] HDR skybox loaded successfully, environment:', !!this.scene.environment);
            if (!this.skyboxEnabled) {
                this.applySkyboxState(false, this.scene);
            }
        } catch (error) {
            console.warn('[VisionaryScene] Failed to load HDR skybox:', error);
            // 如果天空盒加载失败，设置默认深灰色背景
            if (this.scene) {
                this.hdrEnvMap = null;
                this.hdrBackground = new THREE.Color(this.defaultBackgroundColor);
                this.applySkyboxState(this.skyboxEnabled, this.scene);
            }
        }
    }

    /**
     * 开关 HDR 天空盒（环境贴图），可外部调用
     * @param targetScene 指定场景，不传则使用当前场景
     */
    public setSkyboxEnabled(enabled: boolean, targetScene?: THREE.Scene | null): void {
        this.skyboxEnabled = enabled;
        this.applySkyboxState(enabled, targetScene ?? this.scene);
    }

    public async setSkyboxUrl(url: string): Promise<void> {
        if (this.skyboxUrl === url && this.hdrEnvMap) {
            return;
        }
        this.skyboxUrl = url;
        this.disposeSkyboxTextures();
        if (!this.skyboxEnabled || !this.scene) {
            return;
        }
        await this.setupHDRSkybox(url);
        await this.applySkyboxState(true, this.scene);
        this.updateMaterialsEnvironment();
    }

    public async setSkyboxMap(map: { front: string; back: string; left: string; right: string; top: string; bottom: string }): Promise<void> {
        if (!this.scene || !this.renderer) {
            return;
        }
        const urls = [map.right, map.left, map.top, map.bottom, map.front, map.back];
        const loader = new THREE.CubeTextureLoader();
        const texture = loader.load(urls);
        this.scene.background = texture;
        this.scene.environment = texture;
        this.updateMaterialsEnvironment();
    }
    public async setSkyboxSphereMap(map: string): Promise<void> {
        if (!this.scene || !this.renderer) {
            return;
        }
        this.disposeSkyboxTextures();
        if (!this.skyboxEnabled) {
            return;
        }
        try {
            const loader = new THREE.TextureLoader();
            let texture = await loader.loadAsync(map);
            texture.mapping = THREE.EquirectangularReflectionMapping;
            texture.colorSpace = THREE.SRGBColorSpace;
            texture = this.downscaleEquirectangularTexture(texture);

            const envMap = await EnvMapHelper.createPMREMEnvironmentMap(this.renderer, texture) || texture;

            EnvMapHelper.setupSceneEnvironment(this.scene, envMap, texture);
            this.hdrEnvMap = envMap;
            this.hdrBackground = texture;

            EnvMapHelper.setupRendererToneMapping(this.renderer);
            EnvMapHelper.updateRendererEnvironment(this.renderer, this.scene);

            await this.applySkyboxState(true, this.scene);
            this.updateMaterialsEnvironment();
        } catch (error) {
            console.warn('[VisionaryScene] Failed to load equirectangular skybox:', error);
            if (this.scene) {
                this.hdrEnvMap = null;
                this.hdrBackground = new THREE.Color(this.defaultBackgroundColor);
                await this.applySkyboxState(this.skyboxEnabled, this.scene);
            }
        }
    }

    public getSkyboxSettings(): { enabled: boolean; url: string } {
        return { enabled: this.skyboxEnabled, url: this.skyboxUrl };
    }

    ////////   heatmap 相关方法     /////////////////////////////////////////////////////////////////////////
    public setWeightOverlayBlendWithBase(blendWithBase: boolean) {
        if (!this.scene) return;
        if (this.gaussianThreeJSRenderer) {
            this.gaussianThreeJSRenderer.setWeightOverlayBlendWithBase(blendWithBase);
        }
    }

    public setWeightOverlayVisualOptions(options: { gain: number; colormap?: number }) {
        if (!this.scene) return;
        if (this.gaussianThreeJSRenderer) {
            this.gaussianThreeJSRenderer.setWeightOverlayVisualOptions(options);
        }
    }

    public updateHeatmapPipeline(plyModel: PlyModel | undefined): void {
        if (!this.gaussianThreeJSRenderer) return;
        if (!plyModel || !plyModel.weightQuery64 || !plyModel.weightGram64x64 || !plyModel.codebook) {
            this.disableHeatmapPipeline();
            return
        };
        if (plyModel.weightQuery64.length !== 64 || plyModel.weightGram64x64.length !== 64 * 64 || plyModel.codebook.length !== 512 * 64) {
            console.error('[VisionaryScene] update Heatmap Pipeline error: plyModel is not valid');
            this.disableHeatmapPipeline();
            return;
        }
        // if (plyModel.weightQuery64.length === 0 || plyModel.weightGram64x64.length === 0 || plyModel.codebook.length === 0) return;
        try {
            if (typeof (this.gaussianThreeJSRenderer as any).setWeightQueryCosineData === 'function') {
                (this.gaussianThreeJSRenderer as any).setWeightQueryCosineData(plyModel.weightQuery64!, plyModel.weightGram64x64!);
            } else if (typeof this.gaussianThreeJSRenderer.setWeightQueryWeights === 'function') {
                this.gaussianThreeJSRenderer.setWeightQueryWeights(plyModel.weightQuery64!);
            }
            const heatmapParams = globalTools.rightPanelTools?.heatmapPanelTools?.getHeatmapControlState();
            if (!heatmapParams) return;
            // Apply blend mode (base mix vs heatmap-only)
            try {
                if (this.gaussianThreeJSRenderer?.setWeightOverlayBlendWithBase) {
                    this.gaussianThreeJSRenderer.setWeightOverlayBlendWithBase(heatmapParams.blendBase);
                }
                this.gaussianThreeJSRenderer?.setWeightOverlayVisualOptions?.({
                    gain: heatmapParams.heatmapGain,
                    colormap: heatmapParams.colormap,// 0:Turbo,1:Viridis,2:Inferno,3:Magma,4:Plasma,5:Jet
                });
            } catch { }

            // Ensure mutual exclusion of algorithms
            if (this.gaussianThreeJSRenderer?.setWeightOverlayEnabled) {
                this.gaussianThreeJSRenderer.setWeightOverlayEnabled(false);
            }
            if (this.gaussianThreeJSRenderer?.setWeightOverlayApprox2Enabled) {
                this.gaussianThreeJSRenderer.setWeightOverlayApprox2Enabled(false);
            }

            // Default: per-GS similarity heatmap
            try { this.gaussianThreeJSRenderer.setWeightOverlayUseRelevancy?.(heatmapParams.negRelevancy, heatmapParams.relevancyTau); } catch { }
            if (heatmapParams.negRelevancy) {
                this.applyNegRelevancyDataIfPossible(plyModel);
            }
            if (this.gaussianThreeJSRenderer?.setWeightOverlayEnabled) {
                this.gaussianThreeJSRenderer.setWeightOverlayEnabled(true, {
                    threshold: heatmapParams.threshold,
                    opacity: heatmapParams.opacity,
                });
            } else if (this.gaussianThreeJSRenderer?.setWeightOverlayApprox2Enabled) {
                // 兼容部分 core 版本：仅暴露 Approx2 接口
                this.gaussianThreeJSRenderer.setWeightOverlayApprox2Enabled(true, {
                    threshold: heatmapParams.threshold,
                    opacity: heatmapParams.opacity,
                });
            } else {
                console.warn('[VisionaryScene] Heatmap renderer API unavailable: no overlay enable method found');
                this.disableHeatmapPipeline();
            }
        } catch (error) {
            console.error('[VisionaryScene] update Heatmap Pipeline error:', error);
            return;
        }
    }

    private applyNegRelevancyDataIfPossible(plyModel: PlyModel): void {
        if (!this.gaussianThreeJSRenderer?.setWeightOverlayRelevancyData) return;
        if (!plyModel.weightQuery64 || !plyModel.weightGram64x64) return;
        const queryParams = globalTools.rightPanelTools?.heatmapPanelTools?.getQueryParams();
        if (!queryParams) return;
        if (!plyModel.codebook || !queryParams.languageQueries || !queryParams.heatmapQueryName) return;

        const names = ['object', 'things', 'stuff', 'texture'];
        const pos = plyModel.weightQuery64;
        const packed = new Float32Array(5 * 64);
        packed.set(pos, 0);

        let negMask = 0;
        for (let i = 0; i < names.length; i++) {
            const n = names[i];
            if (n === queryParams.heatmapQueryName) continue;
            const q = queryParams.languageQueries.find(x => x?.name === n);
            if (!q || !Array.isArray(q.vector) || q.vector.length !== 512) continue;
            const q64 = computeQueryWeights(plyModel.codebook, new Float32Array(q.vector));
            packed.set(q64, (1 + i) * 64);
            negMask |= (1 << i);
        }

        this.gaussianThreeJSRenderer.setWeightOverlayRelevancyData(packed, plyModel.weightGram64x64, negMask);
    }

    /**
     * 应用天空盒状态到场景
     */
    private async applySkyboxState(enabled: boolean, scene: THREE.Scene | null): Promise<void> {
        if (!scene) return;
        if (enabled) {
            if (!this.hdrEnvMap || !this.hdrBackground) {
                console.warn('no hdrEnvMap or hdrBackground');
                // 未加载完成则尝试加载，加载完成后会根据开关状态再处理
                await this.setupHDRSkybox();
                // 环境贴图加载完成后，更新所有已存在的材质
                this.updateMaterialsEnvironment();
                return;
            }
            scene.environment = this.hdrEnvMap;
            scene.background = this.hdrBackground;
        } else {
            scene.environment = null;
            scene.background = new THREE.Color(this.defaultBackgroundColor);
        }

        if (this.renderer) {
            EnvMapHelper.updateRendererEnvironment(this.renderer, scene);
        }

        // 同步录制相机的天空盒替换开关
        this.objects.forEach(obj => {
            if (obj.type === 'recordingCamera' && obj.object?.userData?.recordingCamera?.setSkyboxEnabled) {
                obj.object.userData.recordingCamera.setSkyboxEnabled(enabled);
            }
        });

        if (this.syncRecordingCamera?.setSkyboxEnabled)
            this.syncRecordingCamera.setSkyboxEnabled(enabled);
        if (this.activeRecordingPreviewCamera?.setSkyboxEnabled)
            this.activeRecordingPreviewCamera.setSkyboxEnabled(enabled);
    }

    /**
     * 更新场景中所有材质的环境贴图引用
     * 当环境贴图加载完成后，确保所有已存在的材质都能使用它
     * 在 WebGPU 渲染器中，需要显式地更新材质以获取环境贴图
     */
    private updateMaterialsEnvironment(): void {
        if (!this.scene || !this.scene.environment || !this.renderer) {
            return;
        }

        let updatedCount = 0;
        this.scene.traverse((object: THREE.Object3D) => {
            if (object instanceof THREE.Mesh && object.material) {
                const materials = Array.isArray(object.material) ? object.material : [object.material];
                materials.forEach((material) => {
                    // 对于支持环境贴图的材质类型，确保它们使用场景的环境贴图
                    if (material instanceof THREE.MeshStandardMaterial ||
                        material instanceof THREE.MeshPhysicalMaterial) {
                        // 在 Three.js WebGPU 中，材质应该自动从 scene.environment 获取环境贴图
                        // 但为了确保更新，我们需要：
                        // 1. 强制材质重新编译
                        material.needsUpdate = true;
                        // 2. 在 WebGPU 中，可能需要显式地设置 envMapIntensity
                        if (material.envMapIntensity === undefined || material.envMapIntensity === 0) {
                            material.envMapIntensity = 1.0;
                        }
                        // 3. 确保材质知道场景有环境贴图（虽然通常不需要手动设置）
                        // 在 WebGPU 中，材质会自动从 scene.environment 获取，但强制更新可以确保同步
                        updatedCount++;
                    }
                });
            }
        });

        console.log(`[VisionaryScene] 已更新 ${updatedCount} 个材质的环境贴图引用`);
    }

    setAmbientLightColor(color: string) {
        if (!this.scene || !this.ambientLight) return;
        this.ambientLight.color.set(color);
    }
    setAmbientLightIntensity(intensity: number) {
        if (!this.scene || !this.ambientLight) return;
        this.ambientLight.intensity = intensity;
    }
    setDirectionalLightColor(color: string) {
        if (!this.scene || !this.directionalLight) return;
        this.directionalLight.color.set(color);
    }
    setDirectionalLightIntensity(intensity: number) {
        if (!this.scene || !this.directionalLight) return;
        this.directionalLight.intensity = intensity;
    }
    setDirectionalLightEnabled(enabled: boolean) {
        if (!this.scene) return;
        if (enabled) {
            if (!this.directionalLight) {
                this.directionalLight = new THREE.DirectionalLight(globalConfig.lightSettings.mainDirectionalLight.color, globalConfig.lightSettings.mainDirectionalLight.intensity);
            }
            // 检查光源是否已在场景中，避免重复添加
            this.directionalLight.visible = true;
            this.directionalLight.castShadow = true;
            this.directionalLight.intensity = globalConfig.lightSettings.mainDirectionalLight.intensity;
        } else {
            if (this.directionalLight && this.directionalLight.parent === this.scene) {
                this.directionalLight.visible = false;
                this.directionalLight.castShadow = false;
                this.directionalLight.intensity = 0;
            }
        }
    }
    setDirectionalLightPosition(x: number, y: number, z: number) {
        if (!this.scene || !this.directionalLight) return;
        this.directionalLight.position.set(x, y, z);
    }
    setSkyboxSettingsEnabled(enabled: boolean) {
        if (!this.scene) return;
        this.skyboxEnabled = enabled;
    }

    public restorePrimitiveFromSerialized(typeTag: string, id: string, name?: string, params?: Record<string, any>) {
        if (!this.scene) return null;

        const geometryInfo = params?.geometry || {};
        const geometryType = geometryInfo.type || typeTag;
        const geometryParams = geometryInfo.parameters || {};
        const ensureNumber = (value: any, fallback: number) => {
            const num = Number(value);
            return Number.isFinite(num) ? num : fallback;
        };

        let geometry: THREE.BufferGeometry | null = null;
        if (typeTag === 'primitive.box' || geometryType === 'BoxGeometry') {
            const width = ensureNumber(geometryParams.width ?? geometryParams.size ?? 1, 1);
            const height = ensureNumber(geometryParams.height ?? geometryParams.size ?? 1, 1);
            const depth = ensureNumber(geometryParams.depth ?? geometryParams.size ?? 1, 1);
            geometry = new THREE.BoxGeometry(width, height, depth);
        } else if (typeTag === 'primitive.sphere' || geometryType === 'SphereGeometry') {
            const radius = ensureNumber(geometryParams.radius ?? 0.5, 0.5);
            const widthSegments = Math.max(3, Math.floor(ensureNumber(geometryParams.widthSegments ?? 16, 16)));
            const heightSegments = Math.max(2, Math.floor(ensureNumber(geometryParams.heightSegments ?? 12, 12)));
            geometry = new THREE.SphereGeometry(radius, widthSegments, heightSegments);
        } else if (geometryType === 'CylinderGeometry') {
            const radiusTop = ensureNumber(geometryParams.radiusTop ?? 0.5, 0.5);
            const radiusBottom = ensureNumber(geometryParams.radiusBottom ?? 0.5, 0.5);
            const height = ensureNumber(geometryParams.height ?? 1, 1);
            const radialSegments = Math.max(3, Math.floor(ensureNumber(geometryParams.radialSegments ?? 12, 12)));
            geometry = new THREE.CylinderGeometry(radiusTop, radiusBottom, height, radialSegments);
        }

        if (!geometry) {
            geometry = new THREE.BoxGeometry(1, 1, 1);
        }

        const materialInfo = params?.material || {};
        let colorHex = 0xffffff;
        if (typeof materialInfo.color === 'number') {
            colorHex = materialInfo.color;
        } else if (materialInfo.color !== undefined) {
            try {
                const color = new THREE.Color(materialInfo.color as any);
                colorHex = color.getHex();
            } catch (error) {
                colorHex = 0xffffff;
            }
        }
        const opacity = ensureNumber(materialInfo.opacity ?? 1, 1);
        const transparent = materialInfo.transparent !== undefined ? !!materialInfo.transparent : opacity < 1;
        const wireframe = !!materialInfo.wireframe;
        const material = new THREE.MeshBasicMaterial({
            color: colorHex,
            opacity,
            transparent,
            wireframe
        });

        const mesh = new THREE.Mesh(geometry, material);
        if (id) {
            mesh.uuid = id;
        }
        mesh.name = name || params?.name || mesh.name || 'Mesh';
        mesh.userData = {
            ...(mesh.userData || {}),
            type: 'mesh',
            serializedTypeTag: typeTag
        };

        this.scene.add(mesh);
        const inserted = this.handleObjectsPush(new VisionaryObject(mesh.uuid, mesh.name, 'mesh', mesh, null as any, ''));
        if (inserted) {
            inserted.id = mesh.uuid;
            inserted.name = mesh.name;
        }
        return mesh;
    }

    // 恢复录制相机信息 - 读取scene.json中的录制相机信息
    public restoreRecordingCameraFromSerialized(id: string, name?: string, trs?: [number[], number[], number[]], params?: Record<string, any>) {
        if (!this.scene) return null;

        const cameraParams = params?.camera || {};
        const viewport = params?.viewport || {};
        const preview = params?.preview || {};
        const gizmo = params?.gizmo || {};
        let cameraName = name || '录制相机';
        const ensureNumber = (value: any, fallback: number) => {
            const num = Number(value);
            return Number.isFinite(num) ? num : fallback;
        };

        const width = ensureNumber(viewport.width ?? 1920, 1920);
        const height = ensureNumber(viewport.height ?? 1080, 1080);
        const fov = ensureNumber(cameraParams.fov ?? 55, 55);
        const previewVisible = preview.visible !== undefined ? !!preview.visible : true;

        const recordingCamera = new RecordingCamera(this.id, width, height, fov, previewVisible, cameraName, false);
        const sceneObject = recordingCamera.getSceneObject();
        if (!sceneObject) {
            return null;
        }

        if (id) {
            sceneObject.uuid = id;
        }
        sceneObject.name = cameraName || name || sceneObject.name || 'RecordingCamera';

        if (cameraParams.near !== undefined) {
            recordingCamera.camera.near = ensureNumber(cameraParams.near, recordingCamera.camera.near);
        }
        if (cameraParams.far !== undefined) {
            recordingCamera.camera.far = ensureNumber(cameraParams.far, recordingCamera.camera.far);
        }
        if (cameraParams.aspect !== undefined) {
            recordingCamera.camera.aspect = ensureNumber(cameraParams.aspect, recordingCamera.camera.aspect);
        }
        recordingCamera.camera.updateProjectionMatrix();

        if (typeof recordingCamera.setGizmoLength === 'function' && gizmo.length !== undefined) {
            recordingCamera.setGizmoLength(ensureNumber(gizmo.length, recordingCamera.getGizmoLength?.() ?? 5));
        }

        if (previewVisible) {
            if (typeof recordingCamera.ensurePreviewWindow === 'function') {
                recordingCamera.ensurePreviewWindow(i18next.language as string);
            }
        } else if (typeof recordingCamera.hidePreviewWindow === 'function') {
            recordingCamera.hidePreviewWindow();
        }

        this.scene.add(sceneObject);
        const inserted = this.handleObjectsPush(new VisionaryObject(sceneObject.uuid, sceneObject.name, 'recordingCamera', sceneObject, null as any, ''));
        if (inserted) {
            inserted.id = sceneObject.uuid;
            inserted.name = sceneObject.name;
        }

        if (trs) {
            this.applyTRSToObject(sceneObject.uuid, trs);
            if (recordingCamera && typeof recordingCamera.syncCameraFromWrapper === 'function') {
                recordingCamera.syncCameraFromWrapper();
            }
        }

        return { sceneObject, recordingCamera };
    }

    public applyTRSToObject(id: string, trs?: [number[], number[], number[]]) {
        if (!trs || trs.length < 3) return;
        let target = this.objects.find(obj => obj.id === id);
        if (!target) {
            target = this.objects.find(obj => obj.name === id);
        }
        if (!target) return;

        const ensureNumber = (value: any, fallback: number) => {
            const num = Number(value);
            return Number.isFinite(num) ? num : fallback;
        };

        const [translate, rotate, scale] = trs;
        if (Array.isArray(translate) && translate.length >= 3) {
            target.object.position.set(
                ensureNumber(translate[0], 0),
                ensureNumber(translate[1], 0),
                ensureNumber(translate[2], 0)
            );
        }
        if (Array.isArray(rotate) && rotate.length >= 3) {
            target.object.rotation.set(
                ensureNumber(rotate[0], 0),
                ensureNumber(rotate[1], 0),
                ensureNumber(rotate[2], 0)
            );
        }
        if (Array.isArray(scale) && scale.length >= 3) {
            target.object.scale.set(
                ensureNumber(scale[0], 1),
                ensureNumber(scale[1], 1),
                ensureNumber(scale[2], 1)
            );
        }

        this.updateBoundingBox(target.id);
    }

    // is discard ???
    public applyGaussianParams(id: string, gaussianParams: any) {
        if (!gaussianParams) return;
        if (!this.gaussianThreeJSRenderer) return;

        const modelIndex = this.findGaussianModelIndex(id);
        if (modelIndex < 0) return;

        const modelId = `model_${modelIndex}`;
        if (gaussianParams.visible !== undefined && typeof this.gaussianThreeJSRenderer.setModelVisible === 'function') {
            this.gaussianThreeJSRenderer.setModelVisible(modelId, !!gaussianParams.visible);
        }
        if (gaussianParams.gaussianScale !== undefined && typeof this.gaussianThreeJSRenderer.setModelGaussianScale === 'function') {
            this.gaussianThreeJSRenderer.setModelGaussianScale(modelId, gaussianParams.gaussianScale);
        }
        if (gaussianParams.opacityScale !== undefined && typeof this.gaussianThreeJSRenderer.setModelOpacityScale === 'function') {
            this.gaussianThreeJSRenderer.setModelOpacityScale(modelId, gaussianParams.opacityScale);
        }
        if (gaussianParams.maxShDeg !== undefined && typeof this.gaussianThreeJSRenderer.setModelMaxShDeg === 'function') {
            this.gaussianThreeJSRenderer.setModelMaxShDeg(modelId, gaussianParams.maxShDeg);
        }
        if (gaussianParams.timeScale !== undefined && typeof this.gaussianThreeJSRenderer.setModelTimeScale === 'function') {
            this.gaussianThreeJSRenderer.setModelTimeScale(modelId, gaussianParams.timeScale);
        }
        if (gaussianParams.timeOffset !== undefined && typeof this.gaussianThreeJSRenderer.setModelTimeOffset === 'function') {
            this.gaussianThreeJSRenderer.setModelTimeOffset(modelId, gaussianParams.timeOffset);
        }
        if (gaussianParams.isLoop !== undefined && typeof this.gaussianThreeJSRenderer.setModelAnimationIsLoop === 'function') {
            this.gaussianThreeJSRenderer.setModelAnimationIsLoop(modelId, gaussianParams.isLoop);
        }
    }

    // 保存当前所有状态为关键帧 - by helen - 改用渲染相机来记录关键帧，所以不再记录相机信息
    public getSceneFrameInfo(index: number) {
        if (!this.camera) return null;
        let newFrameInfo = new VisFrameInfo(index);
        this.objects.forEach(obj => {
            const cameraParams = this.getRecordingCameraParams(obj.id);
            let newObjectFrameInfo = new VisObjectFrameInfo(obj.id, obj.object.position.clone(), obj.object.rotation.clone(), obj.object.scale.clone(), cameraParams);
            newFrameInfo.objects.push(newObjectFrameInfo);
        });
        return newFrameInfo;
    }

    // 设置对象参数变化的回调
    public setObjectChangeCallback(callback: () => void) {
        this.objectChangeCallback = callback;
    }
    public setSceneResetCallback(callback: () => void) {
        this.sceneResetCallback = callback;
    }
    public setViewport(width: number, height: number) {
        if (!this.camera || !this.renderer) return;

        this.camera.aspect = width / height;
        this.camera.updateProjectionMatrix();
        this.renderer.setSize(width, height);
    }

    createGrid() {
        if (!this.scene) return;

        // 创建网格组
        this.grid = new THREE.Group();
        this.grid.position.y = 0; // 确保在 y=0 平面
        this.grid.name = 'CustomGrid';

        // 创建基础网格（灰白色，不包含中心线）
        // 通过设置相同的颜色来隐藏 GridHelper 的中心线
        const grayColor = 0xaaaaaa;
        this.gridBase = new THREE.GridHelper(2000, 2000, grayColor, grayColor);

        // 设置透明度
        if (Array.isArray(this.gridBase.material)) {
            this.gridBase.material.forEach((mat: THREE.Material) => {
                (mat as THREE.LineBasicMaterial).opacity = 0.5;
                (mat as THREE.LineBasicMaterial).transparent = true;
            });
        } else {
            (this.gridBase.material as THREE.LineBasicMaterial).opacity = 0.5;
            (this.gridBase.material as THREE.LineBasicMaterial).transparent = true;
        }

        this.grid.add(this.gridBase);

        this.scene.add(this.grid);

        // 初始化密度
        this.currentGridDensity = 1;
        this.grid.visible = globalConfig.sceneParams.grid; // 初始化网格可见性
    }

    // 窗口大小变化
    onWindowResize() {
        const container = document.getElementById(this.domid);
        if (!container) return;

        const width = container.clientWidth;
        const height = container.clientHeight;

        this.setViewport(width, height);

        // // 如果有高斯渲染器，通知它resize
        if (this.gaussianThreeJSRenderer && this.gaussianThreeJSRenderer.onResize) {
            this.gaussianThreeJSRenderer.onResize(width, height); // core中暂时关闭，显示深度问题等待优化 by helen 2025-11-28
        }
    }

    private isAnimateContextValid(): boolean {
        return !this.isDestroyed && !!this.camera && !!this.renderer && !!this.scene;
    }

    async animate() {

        if (!this.isAnimateContextValid() || this.isExportingVideo) {
            this.tickRenderFps(performance.now(), false);
            if (this.isLoopAnimation && !this.isDestroyed) {
                this.animationId = requestAnimationFrame(() => this.animate());
            }
            return;
        }

        // 更新相机矩阵
        this.camera.updateMatrixWorld();

        this.executePreRenderCallbacks().catch(error => {
            console.error('Error in pre-render callbacks:', error);
        });

        var render_success = false;
        // 如果有高斯渲染器，使用高斯渲染
        if (this.gaussianThreeJSRenderer) {
            const currentTime = (Date.now() - this.startTime) / 1000.0; // 时间（秒）
            const gaussianRenderer = this.gaussianThreeJSRenderer;
            try {
                // 阻塞等待ONNX推理完成后再渲染
                await gaussianRenderer.updateDynamicModels(this.camera, currentTime);
                if (!this.isAnimateContextValid() || !this.gaussianThreeJSRenderer) {
                    return;
                }
                // 推理完成后才进行渲染
                this.gaussianThreeJSRenderer.renderOverlayScene(this.gizmoOverlayScene as THREE.Scene, this.camera);
                this.gaussianThreeJSRenderer.renderThreeScene(this.camera);
                render_success = this.gaussianThreeJSRenderer.drawSplats(this.renderer, this.scene, this.camera);
            } catch (error) {
                if (!this.isDestroyed) {
                    console.error('Error updating dynamic models:', error);
                }
            }
        }
        if (!render_success) {
            if (!this.isAnimateContextValid()) {
                return;
            }
            // 回退到标准Three.js渲染
            // 保存当前的autoClear状态
            const prevAutoClear = (this.renderer as any).autoClear;

            // 渲染主场景（autoClear=true会清空并渲染）
            this.renderer.render(this.scene, this.camera);

            // 即使没有高斯渲染器，也需要渲染gizmo overlay scene
            if (this.gizmoOverlayScene) {
                // 关闭autoClear，避免清空画布，让gizmo叠加在主场景上
                (this.renderer as any).autoClear = false;
                this.renderer.render(this.gizmoOverlayScene, this.camera);
            }

            // 恢复autoClear状态
            (this.renderer as any).autoClear = prevAutoClear;
        }

        // 更新Gizmo（需要在渲染后调用）
        if (this.gizmoManager) {
            this.gizmoManager.update();
        }

        // 更新所有RecordingCamera的gizmo
        this.updateRecordingCameraGizmos();

        this.tickRenderFps(performance.now(), true);

        // 执行渲染后回调（不等待，避免阻塞动画循环）
        this.executePostRenderCallbacks().catch(error => {
            console.error('Error in post-render callbacks:', error);
        });

        if (this.isLoopAnimation && !this.isDestroyed) {
            this.animationId = requestAnimationFrame(() => this.animate());
        }
    }

    // 更新所有RecordingCamera的gizmo
    private updateRecordingCameraGizmos() {
        if (!this.scene) return;

        this.scene.traverse((object: THREE.Object3D) => {
            if (object.userData && object.userData.type === 'recordingCamera') {
                const recordingCamera = object.userData.recordingCamera;
                if (recordingCamera && typeof recordingCamera.updateGizmo === 'function') {
                    recordingCamera.updateGizmo();
                }
            }
        });
    }

    /**
     * 执行所有渲染前回调
     */
    private async executePreRenderCallbacks(): Promise<void> {
        for (const callback of this.preRenderCallbacks) {
            try {
                await callback();
            } catch (error) {
                console.error('Pre-render callback error:', error);
            }
        }
    }

    /**
     * 订阅渲染 FPS（约每秒一次）。fps 为 null 表示循环空闲（导出 / 停止 / 无效）
     */
    public setOnFPSUpdate(callback: ((fps: number | null) => void) | null): void {
        this.onFPSUpdate = callback;
        this.fpsFrames = 0;
        this.fpsWindowStart = 0;
        this.fpsIdleNotified = false;
    }

    /**
     * 统计已完成的 animate 帧。rendered=false 时只通知一次空闲
     */
    private tickRenderFps(now: number, rendered: boolean): void {
        if (this.isDestroyed || !this.onFPSUpdate) {
            return;
        }
        if (!rendered) {
            this.fpsFrames = 0;
            this.fpsWindowStart = 0;
            if (!this.fpsIdleNotified) {
                this.fpsIdleNotified = true;
                this.onFPSUpdate(null);
            }
            return;
        }
        this.fpsIdleNotified = false;
        if (this.fpsWindowStart === 0) {
            this.fpsWindowStart = now;
            this.fpsFrames = 0;
        }
        this.fpsFrames++;
        const elapsed = now - this.fpsWindowStart;
        if (elapsed >= 1000) {
            const fps = Math.round((this.fpsFrames * 1000) / elapsed);
            this.onFPSUpdate(fps);
            this.fpsFrames = 0;
            this.fpsWindowStart = now;
        }
    }

    /**
     * 添加渲染后回调
     */
    public addPostRenderCallback(callback: () => void | Promise<void>): void {
        if (!this.postRenderCallbacks.includes(callback)) {
            this.postRenderCallbacks.push(callback);
        }
    }

    /**
     * 移除渲染后回调
     */
    public removePostRenderCallback(callback: () => void | Promise<void>): void {
        const index = this.postRenderCallbacks.indexOf(callback);
        if (index > -1) {
            this.postRenderCallbacks.splice(index, 1);
        }
    }

    private async activateRecordingCameraPreview(recordingCamera: RecordingCamera): Promise<void> {
        if (!this.scene || !this.renderer) {
            console.warn('[VisionaryScene] 无法启动录制相机预览：scene 或 renderer 未初始化');
            return;
        }
        console.log('激活录制相机预览', recordingCamera, this.activeRecordingPreviewCamera == recordingCamera)
        if (this.activeRecordingPreviewCamera === recordingCamera) {
            console.log('[VisionaryScene] 录制相机预览已激活，跳过重复激活');
            await this.updateRecordingCameraGaussianModels();
            return;
        }

        this.deactivateRecordingCameraPreview(undefined, true);

        // 确保预览窗口已创建
        recordingCamera.ensurePreviewWindow(i18next.language as string);
        // 确保动画循环正在运行
        if (!this.isLoopAnimation) {
            console.log('[VisionaryScene] 启动动画循环以支持录制相机预览');
            this.isLoopAnimation = true;
            this.animate();
        }
        const gaussianModels = (this.gaussianThreeJSRenderer && typeof this.gaussianThreeJSRenderer.getGaussianModels === 'function')
            ? this.gaussianThreeJSRenderer.getGaussianModels()
            : undefined;

        if (!recordingCamera.isInitialized()) { // 没有初始化，则初始化渲染器
            console.log('[VisionaryScene] initializeRenderer 初始化录制相机渲染器...');
            modelDialogTools.showLoadingInfoModal(getI18nText('loadingMessage.initializingRecordingCamera'));

            const initialized = await recordingCamera.initializeRenderer(
                this.renderer,
                this.scene,
                gaussianModels
            );

            if (!initialized) {
                console.warn('[VisionaryScene] 录制相机预览初始化失败');
                return;
            }
            console.log('[VisionaryScene] initializeSuccess 录制相机渲染器初始化成功');

            const callback = async () => {
                if (!recordingCamera.isPreviewVisible()) {
                    console.log('[VisionaryScene] 录制相机预览窗口不可见，停用预览');
                    this.deactivateRecordingCameraPreview(recordingCamera, true);
                    return;
                }

                await recordingCamera.render(this.scene!);
            };

            this.recordingPreviewCallback = callback;
            this.activeRecordingPreviewCamera = recordingCamera;
            this.addPostRenderCallback(callback);
            setTimeout(() => {
                modelDialogTools.closeLoadingInfoModal(1);
            }, 100);
        } else {
            console.log('[VisionaryScene] 录制相机预览已激活，更新高斯模型...');
            const callback = () => {
                if (!recordingCamera.isPreviewVisible()) {
                    console.log('[VisionaryScene] 录制相机预览窗口不可见，停用预览');
                    this.deactivateRecordingCameraPreview(recordingCamera, true);
                    return;
                }
                void recordingCamera.render(this.scene!);
            };

            this.recordingPreviewCallback = callback;
            this.activeRecordingPreviewCamera = recordingCamera;
            this.addPostRenderCallback(callback);
            await this.updateRecordingCameraGaussianModels();
        }

        if (this.activeRecordingPreviewCamera) {
            this.activeRecordingPreviewCamera.showPreviewWindow(this.skyboxEnabled);
        }
    }

    private deactivateRecordingCameraPreview(recordingCamera?: RecordingCamera, force: boolean = false): void {
        if (!this.activeRecordingPreviewCamera) {
            return;
        }

        if (recordingCamera && this.activeRecordingPreviewCamera !== recordingCamera) {
            return;
        }

        if (!force && this.activeRecordingPreviewCamera.isPreviewVisible()) {
            return;
        }

        if (this.recordingPreviewCallback) {
            this.removePostRenderCallback(this.recordingPreviewCallback);
            this.recordingPreviewCallback = null;
        }

        // this.activeRecordingPreviewCamera.updateStatusInfo(getI18nText('recordingCamera.statusViewing'));
        this.activeRecordingPreviewCamera = null;
    }

    /**
     * 激活录制相机预览（公共接口）
     * @param recordingCamera 录制相机对象
     */
    public async showRecordingCameraPreview(recordingCamera: RecordingCamera): Promise<void> {
        return this.activateRecordingCameraPreview(recordingCamera);
    }

    /**
     * 执行所有渲染后回调
     */
    private async executePostRenderCallbacks(): Promise<void> {
        for (const callback of this.postRenderCallbacks) {
            try {
                await callback();
            } catch (error) {
                console.error('Post-render callback error:', error);
            }
        }
    }

    stopAnimation() {
        if (this.animationId) {
            cancelAnimationFrame(this.animationId);
            this.animationId = null;
        }

        this.isLoopAnimation = false;
        this.tickRenderFps(performance.now(), false);
    }

    setGaussianAnimationTime(time: number) {
        if (this.gaussianThreeJSRenderer) {
            this.gaussianThreeJSRenderer.setAllAnimationTime(time); // 需要修改速度 --- helen
            if (this.isExportingVideo) {
                const currentFrame = Math.floor(time * globalConfig.frameRate);
                const progress = (currentFrame / globalConfig.totalFrames) * 100;
                const textcontent = `${currentFrame}/${globalConfig.totalFrames}`;
            }
        }
    }

    // 更新包围盒、gizmo辅助工具等(包围盒已做，后续根据需要判断是否需要优化  by helen)
    private updateBoundingBox(_id: string): void {
        if (!this.scene) return;

        if (!this.gizmoManager && !this.gizmoInitPromise) {
            void this.initializeGizmoManager();
        }
        if (_id == this.currentSelectedObjectId) { // 如果当前选中对象且gizmo启用，则设置目标对象
            if (this.gizmoManager?.getEnabled()) {
                this.gizmoManager?.setTarget(this.objects.find(obj => obj.id == _id)?.object as THREE.Object3D);
            }
            window.dispatchEvent(new CustomEvent('event.modelParamsChange', { detail: { sceneid: this.id, modelid: _id } }));
        }
    }

    /**
     * 同步 RecordingCamera 的相机位置（如果对象是 RecordingCamera）
     * @param obj 要检查的对象
     */
    private syncRecordingCameraIfNeeded(obj: VisionaryObject | undefined, from: string): void {
        if (!obj || !obj.object) return;
        if (obj.object.userData && obj.object.userData.type === 'recordingCamera') {
            const recordingCamera = obj.object.userData.recordingCamera;
            if (recordingCamera && typeof recordingCamera.syncCameraFromWrapper === 'function') {
                recordingCamera.syncCameraFromWrapper();
            }
        }
    }

    private createGizmoModePanel(): void {
        const container = document.getElementById(this.domid);
        if (!container) {
            console.warn('[VisionaryScene] 无法创建 Gizmo 模式面板：容器不存在');
            return;
        }

        if (!this.gizmoManager) {
            return;
        }

        if (this.gizmoModePanel) {
            this.gizmoModePanel.remove();
            this.gizmoModePanel = null;
        }

        if (getComputedStyle(container).position === 'static') {
            container.style.position = 'relative';
        }

        const panel = document.createElement('div');
        panel.id = `${this.domid}-gizmo-mode-panel`;
        panel.className = 'vision-gizmohelper-container';

        const modes: Array<{ id: GizmoMode; label: string; icon?: string; svg?: string }> = [ // 多语言 -- 2025-12-15
            { id: 'translate', label: 'Move(G)', icon: 'MoveOutlined' },
            { id: 'rotate', label: 'Rotate(R)', icon: 'RotateOutlined' },
            { id: 'scale', label: 'Scale(S)', icon: 'ScaleOutlined' }
        ];

        const currentMode = this.currentGizmoMode;

        modes.forEach((modeInfo) => {
            const button = document.createElement('button');
            button.type = 'button';
            button.className = 'visionary-gizmo-mode-btn';
            button.dataset.mode = modeInfo.id;
            button.title = modeInfo.label;
            button.setAttribute('data-mode', modeInfo.id);

            if (modeInfo.icon) {
                const iconSpan = document.createElement('i');
                iconSpan.classList.add('icon', 'iconfont', `icon-${modeInfo.icon}`);
                button.appendChild(iconSpan);
            }
            if (modeInfo.svg) {
                button.innerHTML = modeInfo.svg;
            }

            button.addEventListener('click', this.handleGizmoModeButtonClick.bind(this, modeInfo.id));

            panel.appendChild(button);
        });

        container.appendChild(panel);
        this.gizmoModePanel = panel;
        this.updateGizmoModePanelActive(currentMode);
    }

    private handleGizmoModeButtonClick(mode: GizmoMode): void {
        if (!this.gizmoManager) return;
        this.setGizmoMode(this.currentGizmoMode != mode ? mode as GizmoMode : 'normal' as GizmoMode);
    }
    private updateGizmoModePanelActive(activeMode: GizmoMode): void {
        if (!this.gizmoModePanel) return;
        this.currentGizmoMode = activeMode;
        const buttons = Array.from(this.gizmoModePanel.querySelectorAll<HTMLButtonElement>('.visionary-gizmo-mode-btn'));
        buttons.forEach((btn) => {
            const mode = btn.dataset.mode as GizmoMode | undefined;
            if (!mode) return;

            if (mode === activeMode) {
                btn.classList.add('active');
            } else {
                btn.classList.remove('active');
            }
        });
    }

    setGizmoMode(mode: GizmoMode) {
        if (!this.gizmoManager) return;
        if (mode === 'normal') {
            this.updateGizmoModePanelActive('normal');
            this.gizmoManager.setEnabled(false);
            this.gizmoManager.setTarget(null);
        } else {
            this.gizmoManager.setMode(mode);
            this.updateGizmoModePanelActive(mode);
            this.gizmoManager.setEnabled(true);
            this.gizmoManager.setTarget(this.currentSelectedObjectId ? this.objects.find(obj => obj.id == this.currentSelectedObjectId)?.object as THREE.Object3D : null);
        }
    }
    setObjectSelected(id: string | null, selected: boolean) {
        if (!this.scene) return;

        if (!this.gizmoManager && !this.gizmoInitPromise) {
            void this.initializeGizmoManager();
        }

        if (id == null) {
            this.currentSelectedObjectId = null;
            this.objects.forEach(obj => {
                obj.object.selected = false;
            });
            this.gizmoManager?.setTarget(null);
            const currentSelected = globalManager.getCurrentSelected();
            if (currentSelected && currentSelected.sceneid === this.id && selected) {
                globalManager.setCurrentSelected(this.id, '');
            }
            //clearObjectSelect in modelview-panel-tools
            window.dispatchEvent(new CustomEvent('event.clearObjectSelect', { detail: { sceneid: this.id } }));
            return;
        }

        const targetObject = this.objects.find(obj => obj.id == id);
        if (!targetObject) return;

        this.currentSelectedObjectId = selected ? id : null;

        // 设置对象的选中状态
        targetObject.object.selected = selected;

        if (!selected) {
            this.gizmoManager?.setTarget(null);
            return;
        }

        this.objects.forEach(obj => {
            if (obj.id !== id) {
                obj.object.selected = false;
            }
        });

        if (this.gizmoManager?.getEnabled()) {
            this.gizmoManager?.setTarget(targetObject.object as THREE.Object3D);
        }
    }
    getObjects() {
        return this.objects;
    }
    getObjectById(id: string) {
        return this.objects.find(obj => obj.id == id);
    }

    getCamera() {
        return this.camera;
    }
    getScene() {
        return this.scene;
    }

    getCameraParams() {
        return {
            position: this.camera?.position,
            rotation: {
                x: this.camera?.rotation.x,
                y: this.camera?.rotation.y,
                z: this.camera?.rotation.z,
            },
            scale: this.camera?.scale,
            fov: this.camera?.fov,
            near: this.camera?.near,
            far: this.camera?.far,
        };
    }

    /**
     * 设置网格可见性
     * @param visible 是否可见
     */
    public setGridVisible(visible: boolean): void {
        if (this.grid) {
            this.grid.visible = visible;
        }
    }

    getRenderer() {
        return this.renderer;
    }

    public getGizmoManager(): GizmoManager | null {
        return this.gizmoManager;
    }

    public getId(): string {
        return this.id;
    }

    public onGizmoManagerReady(callback: (gizmo: GizmoManager) => void): void {
        if (this.gizmoManager) {
            callback(this.gizmoManager);
        } else {
            this.gizmoReadyCallbacks.push(callback);
        }
    }

    // 设置渲染模式 需要对接Core中的渲染模式 - 0-普通，1-法线，2-深度；点云暂时不支持，后续增加 - by helen
    setRenderMode(mode: number) {
        const isHeatmap = mode === globalConfig.renderMode.heatmap;

        this.disableHeatmapPipeline();
        if (isHeatmap) {
            // Heatmap is an overlay mode; keep base rendering in normal mode.
            if (this.gaussianThreeJSRenderer) {
                this.objects.forEach(obj => {
                    if (obj.type === 'visionary') {
                        const modelIndex = this.findGaussianModelIndex(obj.object.uuid);
                        if (modelIndex >= 0) {
                            this.gaussianThreeJSRenderer.setModelRenderMode(`model_${modelIndex}`, globalConfig.renderMode.normal);
                        }
                    }
                });
            }
            this.updateHeatmapPipelineById();
            return;
        }
        if (this.gaussianThreeJSRenderer) {
            this.objects.forEach(obj => {
                if (obj.type === 'visionary') {
                    const modelIndex = this.findGaussianModelIndex(obj.object.uuid);
                    if (modelIndex >= 0) {
                        this.gaussianThreeJSRenderer.setModelRenderMode(`model_${modelIndex}`, mode);
                    }
                }
            });
        }
    }
    private disableHeatmapPipeline() {
        try {
            if (this.gaussianThreeJSRenderer) {
                if (this.gaussianThreeJSRenderer.setWeightOverlayEnabled) {
                    this.gaussianThreeJSRenderer.setWeightOverlayEnabled(false);
                }
                if (this.gaussianThreeJSRenderer.setWeightOverlayApprox2Enabled) {
                    this.gaussianThreeJSRenderer.setWeightOverlayApprox2Enabled(false);
                }
            }
        } catch (error) {
            console.error('[VisionaryScene] disableHeatmapPipeline error:', error);
        }
    }
    // 公共方法：取消选择所有对象
    public clearSelection() {
        // const currentSelected = globalManager.getCurrentSelected();
        // if (currentSelected && currentSelected.sceneid === this.id) {
        //     globalManager.setCurrentSelected(this.id, '');
        // }

        this.setObjectSelected(null, false);
    }
    // 清空当前选中并关闭录制相机预览窗口
    private clearCurrentSelected() {
        // 关闭录制相机预览窗口
        if (this.syncRecordingCamera) {
            this.syncRecordingCamera.hidePreviewWindow();
        }
        if (this.activeRecordingPreviewCamera) {
            this.activeRecordingPreviewCamera.hidePreviewWindow();
        }
        this.setObjectSelected(null, false);
    }
    // 删除对象
    removeObjectById(id: string) {
        const object = this.objects.find(obj => obj.id == id);
        if (!object) return;
        if (object.type === 'visionary') {
            this.detachProxyForGaussian(object.object as GaussianModel);
            if (this.gaussianThreeJSRenderer && this.gaussianThreeJSRenderer.removeModelById) {
                const modelIndex = this.findGaussianModelIndex(id);
                if (modelIndex >= 0) {
                    this.gaussianThreeJSRenderer.removeModelById(`model_${modelIndex}`);
                }
            }
            this.updateRecordingCameraGaussianModels();
        }
        if (object.isPlyHeatmap) {
            window.dispatchEvent(new CustomEvent('event.plyHeatmapUnloaded', { detail: { modelId: id } }));
        }

        this.selectables = this.selectables.filter(selectable => selectable.uuid !== id);
        this.gaussianProxyMap.delete(object.object as GaussianModel);
        this.scene?.remove(object.object);

        this.objects = this.objects.filter(obj => obj.id !== id);

        // 检查并清除当前选中的对象和gizmo target
        if (this.currentSelectedObjectId === id) {
            this.currentSelectedObjectId = null;
            this.gizmoManager?.setTarget(null);
        }

        const currentSelected = globalManager.getCurrentSelected();
        if (currentSelected && currentSelected.sceneid === this.id && currentSelected.objectid === id) {
            globalManager.setCurrentSelected(this.id, '');
        }
    }
    setObjectVisibility(id: string, visible: boolean) {
        if (!this.scene) return;
        const targetObject = this.objects.find(obj => obj.id == id);
        if (!targetObject) return;
        targetObject.object.visible = visible;
        this.updateBoundingBox(id);
    }
    copyObjectById(id: string) { // 暂时不用
        if (!this.scene) return;
        const targetObject = this.objects.find(obj => obj.id == id);
        if (!targetObject) return;
        const newObject = targetObject.object.clone();
        newObject.position.x = targetObject.object.position.x + 0.1;

        this.scene.add(newObject);
        const file = (targetObject.type === 'visionary' ? cloneFile(targetObject.file) : null) as any;
        this.handleObjectsPush(new VisionaryObject(newObject.uuid, targetObject.name + '_copy', targetObject.type, newObject, file, targetObject.url));
    }
    // 清空场景
    clearScene() {
        if (!this.scene) return;
        this.clearCurrentSelected(); // 清空选中
        this.objects.forEach(obj => {
            this.scene!.remove(obj.object);
        });
        this.objects = [];
    }
    ////////////////////  object参数更新  //////////////////////////////////////////////////
    // 更新对象参数
    updateObjectParams(id: string, params: { position: { x: number, y: number, z: number }, rotation: { x: number, y: number, z: number }, scale: { x: number, y: number, z: number }, gaussianParams: any | null, cameraParams: any | null }) {
        let obj = this.objects.find(obj => obj.id == id);
        if (obj) {
            obj.object.position.set(params.position.x, params.position.y, params.position.z);
            obj.object.rotation.set(params.rotation.x, params.rotation.y, params.rotation.z);
            obj.object.scale.set(params.scale.x, params.scale.y, params.scale.z);

            // ✅ 同步 RecordingCamera 的相机位置
            this.syncRecordingCameraIfNeeded(obj, 'updateObjectParams');

            if (params.gaussianParams) {
                if (this.gaussianThreeJSRenderer) {
                    const modelIndex = this.findGaussianModelIndex(id);
                    if (modelIndex >= 0) {
                        const modelId = `model_${modelIndex}`;
                        this.gaussianThreeJSRenderer.setModelGaussianScale(modelId, params.gaussianParams.gaussianScale);
                        this.gaussianThreeJSRenderer.setModelOpacityScale(modelId, params.gaussianParams.opacityScale);
                        this.gaussianThreeJSRenderer.setModelMaxShDeg(modelId, params.gaussianParams.maxShDeg);
                        this.gaussianThreeJSRenderer.setModelTimeScale(modelId, params.gaussianParams.timeScale);
                        this.gaussianThreeJSRenderer.setModelTimeOffset(modelId, params.gaussianParams.timeOffset);
                    }
                }
            }

            if (params.cameraParams && obj.type === 'recordingCamera') {
                obj.object.userData.recordingCamera.camera.fov = params.cameraParams.fov;
                obj.object.userData.recordingCamera.camera.near = params.cameraParams.near;
                obj.object.userData.recordingCamera.camera.far = params.cameraParams.far;
                obj.object.userData.recordingCamera.camera.aspect = params.cameraParams.aspect;
                obj.object.userData.recordingCamera.camera.updateProjectionMatrix();
                obj.object.userData.recordingCamera.camera.updateMatrixWorld();
                (modelUtils as any).onRecordingCameraChange(obj); // 如果录制相机，同步其他场景的录制预览相机参数
            }
        }
        this.updateBoundingBox(id);
    }

    // 更新对象位置
    updateObjectPosition(id: string, position: { x: number, y: number, z: number }) {
        let obj = this.objects.find(obj => obj.id == id);
        if (obj) {
            obj.object.position.set(position.x, position.y, position.z);
            // ✅ 同步 RecordingCamera 的相机位置
            this.syncRecordingCameraIfNeeded(obj, 'updateObjectPosition');
            this.updateBoundingBox(id);
        }
    }

    // 更新对象缩放
    updateObjectScale(id: string, scale: { x: number, y: number, z: number }) {
        let obj = this.objects.find(obj => obj.id == id);
        if (obj) {
            obj.object.scale.set(scale.x, scale.y, scale.z);
            // ✅ 同步 RecordingCamera 的相机位置
            this.syncRecordingCameraIfNeeded(obj, 'updateObjectScale');
            this.updateBoundingBox(id);
        }
    }

    // 更新对象旋转
    updateObjectRotation(id: string, rotation: { x: number, y: number, z: number }) {
        let obj = this.objects.find(obj => obj.id == id);
        if (obj) {
            obj.object.rotation.set(rotation.x, rotation.y, rotation.z);
            // ✅ 同步 RecordingCamera 的相机位置
            this.syncRecordingCameraIfNeeded(obj, 'updateObjectRotation');
            this.updateBoundingBox(id);
        }
    }
    /////////////////////   获取/设置 模型高斯参数      /////////////////////////////////////////////////////
    /**
     * 通过 VisionaryObject 的 ID (UUID) 查找对应的 GaussianModel 在 gaussianModels 数组中的索引
     * @param visionaryObjectId VisionaryObject 的 ID (UUID)
     * @returns 模型在数组中的索引，如果找不到返回 -1
     */
    private findGaussianModelIndex(visionaryObjectId: string): number {
        if (!this.gaussianThreeJSRenderer) return -1;

        const obj = this.getObjectById(visionaryObjectId);
        if (!obj || obj.type !== 'visionary') return -1;

        // 通过 getGaussianModels 获取模型数组
        const gaussianModels = this.gaussianThreeJSRenderer.getGaussianModels();

        // 查找匹配的模型（通过 UUID 或对象引用）
        const index = gaussianModels.findIndex((model: any) => {
            // 比较 UUID 或对象引用
            return model.uuid === obj.object.uuid || model === obj.object;
        });

        if (index === -1) {
            console.warn(`[VisionaryScene] GaussianModel not found for VisionaryObject ID: ${visionaryObjectId}`);
        }

        return index;
    }

    private findGaussianModelIndexByObject(obj: VisionaryObject): number {
        if (!this.gaussianThreeJSRenderer) return -1;
        const gaussianModels = this.gaussianThreeJSRenderer.getGaussianModels();
        const index = gaussianModels.findIndex((model: any) => {
            return model.uuid === obj.object.uuid || model === obj.object;
        });
        return index;
    }

    /**
     * 
     * @param id 获取模型高斯参数
     * @returns 
     */
    getModelGaussianParams(id: string) {
        let obj = this.objects.find(obj => obj.id == id);
        if (obj) {
            if (obj.type === 'visionary') { // 只有高斯模型才有高斯参数
                if (this.gaussianThreeJSRenderer) {
                    const modelIndex = this.findGaussianModelIndex(id);
                    if (modelIndex >= 0) {
                        const allParams = this.gaussianThreeJSRenderer.getModelParams();
                        if (allParams.models[`model_${modelIndex}`]) {
                            return allParams.models[`model_${modelIndex}`];
                        }
                    }
                }
            } else {
                return null;
            }
        } else {
            console.log('获取模型高斯参数失败，对象不存在', id);
        }
        return null;
    }
    // 获取录制相机参数 - 用于关键帧记录及动画渲染
    getRecordingCameraParams(id: string) {
        let obj = this.objects.find(obj => obj.id == id);
        if (obj) {
            if (obj.type === 'recordingCamera') { // 只有渲染相机才有渲染相机参数
                return {
                    fov: obj.object.userData.recordingCamera.camera.fov,
                    near: obj.object.userData.recordingCamera.camera.near,
                    far: obj.object.userData.recordingCamera.camera.far,
                    aspect: obj.object.userData.recordingCamera.camera.aspect,
                };
            } else {
                return null;
            }
        } else {
            console.log('获取渲染相机参数失败，对象不存在', id);
        }
        return null;
    }
    // 设置模型可见性
    setModelVisible(id: string, visible: boolean) {
        let obj = this.objects.find(obj => obj.id == id);
        if (obj) {
            if (obj.type === 'visionary') {
                if (this.gaussianThreeJSRenderer) {
                    const modelIndex = this.findGaussianModelIndex(id);
                    if (modelIndex >= 0) {
                        this.gaussianThreeJSRenderer.setModelVisible(`model_${modelIndex}`, visible);
                    }
                }
            } else {
                obj.object.visible = visible;
                this.updateBoundingBox(id);
            }
        }
    }

    getModelGaussianScale(modelId: string) {
        if (this.gaussianThreeJSRenderer) {
            const modelIndex = this.findGaussianModelIndex(modelId);
            if (modelIndex >= 0) {
                return this.gaussianThreeJSRenderer.getModelGaussianScale(`model_${modelIndex}`);
            }
        }
        return null;
    }

    setModelGaussianParams(modelId: string, type: 'gaussianScale' | 'opacityScale' | 'maxShDeg' | 'timeScale' | 'timeOffset' | 'maxPixelRadius', value: number) {
        if (this.gaussianThreeJSRenderer) {
            const modelIndex = this.findGaussianModelIndex(modelId);
            if (modelIndex >= 0) {

                switch (type) {
                    case 'gaussianScale': // 高斯大小倍数
                        this.gaussianThreeJSRenderer.setModelGaussianScale(`model_${modelIndex}`, value);
                        break;
                    case 'opacityScale': // - 高斯透明度倍数
                        this.gaussianThreeJSRenderer.setModelOpacityScale(`model_${modelIndex}`, value);
                        break;
                    case 'maxShDeg': // 球谐函数等级
                        this.gaussianThreeJSRenderer.setModelMaxShDeg(`model_${modelIndex}`, value);
                        break;
                    case 'timeScale': // 时间缩放倍数
                        this.gaussianThreeJSRenderer.setModelTimeScale(`model_${modelIndex}`, value);
                        break;
                    case 'timeOffset': // 时间偏移量
                        this.gaussianThreeJSRenderer.setModelTimeOffset(`model_${modelIndex}`, value);
                        break;
                    case 'maxPixelRadius': // 最大像素半径
                        // this.gaussianThreeJSRenderer.setModelMaxPixel(`model_${modelIndex}`, value);
                        break;
                }
            }
        }
        return null;
    }

    // 设置模型高斯参数 - 循环播放
    setModelLoop(modelId: string, value: boolean) {
        let obj = this.objects.find(obj => obj.id == modelId);
        if (obj) {
            obj.isLoop = value;
        }
        if (this.gaussianThreeJSRenderer) {
            const modelIndex = this.findGaussianModelIndex(modelId);
            if (modelIndex >= 0) {
                this.gaussianThreeJSRenderer.setModelAnimationIsLoop(`model_${modelIndex}`, value);
            }
        }
    }
    ///////////////////   录制相机参数更新  ////////////////////////////////////////////////////////////////////////////
    setRecordingParams(modelId: string, type: 'fov' | 'near' | 'far', value: number) {
        let obj = this.objects.find(obj => obj.id == modelId);
        if (obj && obj.type === 'recordingCamera' && obj.object.userData) {
            if (obj.object.userData.recordingCamera) {
                switch (type) {
                    case 'fov':
                        obj.object.userData.recordingCamera.camera.fov = value;
                        break;
                    case 'near':
                        obj.object.userData.recordingCamera.camera.near = value;
                        break;
                    case 'far':
                        obj.object.userData.recordingCamera.camera.far = value;
                        break;
                }
                obj.object.userData.recordingCamera.camera.updateProjectionMatrix();
                obj.object.userData.recordingCamera.camera.updateMatrixWorld();
            }
        }
    }

    getRecordingCameraById(modelId: string) {
        let obj = this.objects.find(obj => obj.id == modelId);
        if (obj && obj.type === 'recordingCamera') {
            if (obj.object.userData.recordingCamera) {
                return obj.object.userData.recordingCamera;
            }
        }
        return null;
    }

    syncRecordingCameraParams(type: 'position' | 'rotation' | 'scale' | 'fov' | 'near' | 'far', value: any) {
        if (!this.syncRecordingCamera) return;
        switch (type) {
            case 'position':
                this.syncRecordingCamera?.camera.position.set(value.x, value.y, value.z);
                break;
            case 'rotation':
                this.syncRecordingCamera.camera.rotation.set(value.x, value.y, value.z);
                break;
            case 'scale':
                this.syncRecordingCamera.camera.scale.set(value.x, value.y, value.z);
                break;
            case 'fov':
                this.syncRecordingCamera.camera.fov = value;
                break;
            case 'near':
                this.syncRecordingCamera.camera.near = value;
                break;
            case 'far':
                this.syncRecordingCamera.camera.far = value;
                break;
        }
        this.syncRecordingCamera.camera.updateProjectionMatrix();
        this.syncRecordingCamera.camera.updateMatrixWorld();
        // console.error('同步录制相机参数-----', this.getId(), this.syncRecordingCamera, type, value);
    }

    ////////////////////  camera参数更新  //////////////////////////////////////////////////
    // 更新相机参数
    updateCameraParams(position: { x: number, y: number, z: number }, rotation: { x: number, y: number, z: number }, scale: { x: number, y: number, z: number }) {
        if (!this.camera) return;

        this.camera.position.set(position.x, position.y, position.z);
        this.camera.rotation.set(rotation.x, rotation.y, rotation.z);
        this.camera.scale.set(scale.x, scale.y, scale.z);
    }

    // 更新相机FOV
    updateCameraFOV(fov: number) {
        if (!this.camera) return;
        this.camera.fov = fov;
        this.camera.updateProjectionMatrix();
        this.camera.updateMatrixWorld();
    }

    // 更新相机位置
    updateCameraPosition(position: { x: number, y: number, z: number }) {
        if (!this.camera) return;

        this.camera.position.set(position.x, position.y, position.z);
    }
    // 更新相机参数
    updateCamera(position: { x: number, y: number, z: number }, rotation: { x: number, y: number, z: number }, scale: { x: number, y: number, z: number }) {
        if (!this.camera || !this.renderer || !this.scene) return;

        this.camera.position.set(position.x, position.y, position.z);
        this.camera.rotation.set(rotation.x, rotation.y, rotation.z);
        this.camera.scale.set(scale.x, scale.y, scale.z);
    }
    updateCameraProjectionMatrix(afterCallback: () => void) {
        if (!this.camera || !this.renderer || !this.scene) return;

        this.camera.updateProjectionMatrix();

        if (afterCallback && typeof afterCallback === 'function') {
            afterCallback();
        }
    }

    private async renderSceneToCanvas(width: number, height: number): Promise<HTMLCanvasElement> {
        if (!this.renderer || !this.scene || !this.camera) {
            throw new Error("Renderer, scene, or camera not available");
        }

        const renderer = this.renderer;
        const scene = this.scene;
        const camera = this.camera;

        // 保存当前渲染器状态
        const originalViewportSize = new THREE.Vector2();
        renderer.getSize(originalViewportSize);
        const rendererAny = renderer as any;
        const originalPixelRatio = typeof rendererAny.getPixelRatio === 'function'
            ? rendererAny.getPixelRatio()
            : 1;
        const originalAspect = camera.aspect;

        this.hideEditorHelpers();

        try {
            // 设置新的渲染尺寸
            if (typeof rendererAny.setPixelRatio === 'function') {
                rendererAny.setPixelRatio(1); // 确保像素比率为1，避免模糊
            }
            renderer.setSize(width, height, false); // 不更新 DOM 样式，避免画面闪动

            // 更新相机宽高比
            camera.aspect = width / height;
            camera.updateProjectionMatrix();

            // 更新动态模型 - 阻塞等待ONNX推理完成后再渲染
            if (this.gaussianThreeJSRenderer) {
                const currentTime = (Date.now() - this.startTime) / 1000.0; // 时间（秒）
                await this.gaussianThreeJSRenderer.updateDynamicModels(camera, currentTime);
            }

            // 渲染场景
            if (this.gaussianThreeJSRenderer) {
                this.gaussianThreeJSRenderer.renderThreeScene(camera);
                this.gaussianThreeJSRenderer.drawSplats(renderer, scene, camera);
            } else {
                renderer.render(scene, camera);
            }

            // 将渲染结果拷贝到独立画布，避免恢复尺寸后丢失内容
            const sourceCanvas = renderer.domElement;
            const exportCanvas = document.createElement('canvas');
            exportCanvas.width = width;
            exportCanvas.height = height;
            const exportCtx = exportCanvas.getContext('2d');
            if (!exportCtx) {
                throw new Error('无法创建导出画布上下文');
            }
            exportCtx.drawImage(sourceCanvas, 0, 0, width, height);

            return exportCanvas;
        } catch (error) {
            throw new Error(`导出图片失败: ${(error as Error).message}`);
        } finally {
            // 恢复原始设置
            if (typeof rendererAny.setPixelRatio === 'function') {
                rendererAny.setPixelRatio(originalPixelRatio);
            }
            renderer.setSize(originalViewportSize.x, originalViewportSize.y, false);
            camera.aspect = originalAspect;
            camera.updateProjectionMatrix();

            this.restoreEditorHelpers();

            // 恢复时也需要更新动态模型并渲染
            if (this.gaussianThreeJSRenderer) {
                const currentTime = (Date.now() - this.startTime) / 1000.0; // 时间（秒）
                this.gaussianThreeJSRenderer.updateDynamicModels(camera, currentTime).then(() => {
                    this.gaussianThreeJSRenderer.renderThreeScene(camera);
                    this.gaussianThreeJSRenderer.drawSplats(renderer, scene, camera);
                }).catch((error: Error) => {
                    console.error('Error updating dynamic models in finally block:', error);
                });
            } else {
                renderer.render(scene, camera);
            }
        }
    }

    private setExportingVideoActive(active: boolean, resolution?: { width: number, height: number }) {
        const container = document.getElementById(this.domid);
        if (!container) {
            console.warn('[VisionaryScene] 无法创建 Gizmo 模式面板：容器不存在');
            return;
        }
        if (active) {
            container.classList.add('video-exporting-active');
        } else {
            container.classList.remove('video-exporting-active');
        }
        const currentSelected = globalManager.getCurrentSelected();
        if (currentSelected && currentSelected.sceneid === this.id) {
            if (this.activeRecordingPreviewCamera && resolution) {
                this.activeRecordingPreviewCamera.onResize(active, resolution);
            }
        } else {
            if (this.syncRecordingCamera && resolution) {
                this.syncRecordingCamera.onResize(active, resolution);
            }
        }

        this.isExportingVideo = active;
    }

    /**
     * 导出当前场景的图片并下载
     * @param width 图片宽度
     * @param height 图片高度
     * @param filename 文件名，默认为 'scene-export.png'
     * @param format 图片格式，默认为 'image/png'
     * @param quality 图片质量，仅对 jpeg 格式有效，范围 0-1，默认为 0.9
     */
    public async exportImageAndDownload(width: number, height: number, isLineDrawing: boolean = false): Promise<void> {
        const options = {
            width: width,
            height: height,
            filename: 'scene-export.png',
            format: 'image/png',
            quality: 0.9
        };

        try {
            const exportCanvas = await this.renderSceneToCanvas(options.width, options.height);
            if (isLineDrawing) {
                // 尝试使用 AI 线稿提取
                try {
                    // 调用新的线稿处理方法
                    const lineDrawingDataURL = await mediaController.convertToLineDrawing(exportCanvas);

                    // Save the processed image
                    const lineFilename = options.filename.replace(/(\.[\w\d]+)$/, '_line_drawing.png');

                    // 创建下载链接
                    const link = document.createElement('a');
                    link.href = lineDrawingDataURL;
                    link.download = lineFilename;

                    // 触发下载
                    document.body.appendChild(link);
                    link.click();
                    document.body.removeChild(link);

                    modelDialogTools.closeLoadingInfoModal();
                    console.log(`AI 线稿已导出并下载: ${lineFilename}`);
                    return; // 如果成功，就不执行普通下载了

                } catch (aiError) {
                    console.warn('AI 线稿提取失败或跳过，使用普通导出:', aiError);
                    modelDialogTools.showLoadingInfoModal(getI18nText('loadingMessage.aiLineDrawingFailed'));
                }
            }
            const dataURL = exportCanvas.toDataURL(options.format, options.quality);
            // 创建下载链接
            const link = document.createElement('a');
            link.href = dataURL;
            link.download = options.filename;

            // 触发下载
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
        } catch (error) {
            console.error('导出并下载图片失败:', error);
            modelDialogTools.closeLoadingInfoModal();
            throw error;
        } finally {
            modelDialogTools.closeLoadingInfoModal();
        }
    }

    // 导出视频并下载
    public async exportVideoAndDownload(
        options: VideoExportOptions,
        animationController?: ITimelineController
    ): Promise<void> {
        try {
            // 验证和记录 Gaussian 渲染器状态
            console.log('[VideoExport] 检查 Gaussian 渲染器状态（单视窗模式）...');
            console.log('[VideoExport] this.gaussianThreeJSRenderer:', this.gaussianThreeJSRenderer ? '存在' : '不存在');
            modelDialogTools.showLoadingInfoModal(getI18nText('loadingMessage.preparingVideoExport'));
            // 启用调试日志
            // (globalThis as any).GS_VIDEO_EXPORT_DEBUG = true;

            if (!this.gaussianThreeJSRenderer) {
                console.warn('[VideoExport] 警告：左侧场景没有 Gaussian 渲染器');
                console.warn('[VideoExport] 这可能导致录制的视频中无法看到高斯模型');
            } else {
                try {
                    const leftModels = this.gaussianThreeJSRenderer.getGaussianModels();
                    console.log('[VideoExport] 左侧场景高斯模型数量:', leftModels?.length || 0);
                    if (!leftModels || leftModels.length === 0) {
                        console.warn('[VideoExport] 警告：左侧场景的高斯渲染器没有模型');
                    }
                } catch (error) {
                    console.error('[VideoExport] 获取左侧场景高斯模型失败:', error);
                }
            }

            if (!this.renderer || !this.scene) {
                console.error('渲染器或场景未初始化');
                modelDialogTools.showLoadingInfoModal(getI18nText('loadingMessage.rendererNotInitialized'));
                modelDialogTools.closeLoadingInfoModal(2);
                return;
            }

            // 准备录制相机
            let recordingCamera: RecordingCamera | null = null;
            const currentSelected = globalManager.getCurrentSelected();
            if (currentSelected && currentSelected.sceneid === this.id) {
                const targetObject = this.objects.find(obj => obj.id == currentSelected.objectid);
                if (targetObject && targetObject.type === 'recordingCamera') {
                    recordingCamera = targetObject.object.userData.recordingCamera;
                }
            } else {
                recordingCamera = this.syncRecordingCamera;
            }

            if (!recordingCamera) {
                // console.error('[VideoExport] 无法创建或获取录制相机');
                modelDialogTools.showLoadingInfoModal(getI18nText('loadingMessage.cannotGetRecordingCamera'));
                modelDialogTools.closeLoadingInfoModal(2);
                return;
            } else {
                recordingCamera.ensurePreviewWindow(i18next.language as string);
            }
            this.setExportingVideoActive(true, options.resolution);
            recordingCamera.showPreviewWindow(this.skyboxEnabled); // 显示录制相机预览窗口，否则视频导出只有1K

            modelDialogTools.showLoadingInfoModal(getI18nText('loadingMessage.startingVideoExport'));
            this.hideEditorHelpers();

            globalConfig.sceneParams.isPlaying = true;
            let enhancements: RecordingEnhancementOptions | undefined;
            let cleanupCaptureCanvas: (() => void) | null = null;

            const postProcessorSetup = await mediaController.buildVideoFramePostProcessor(recordingCamera, options);

            if (postProcessorSetup) {
                enhancements = postProcessorSetup.enhancements;
                cleanupCaptureCanvas = postProcessorSetup.cleanup;
            }
            // 视频导出配置 - 对接页面参数：分辨率、帧率、比特率
            // 将前端传入的 bitrate 字符串转换为 VideoQuality 枚举
            const bitrateToQuality = (bitrate: string): VideoQuality => {
                switch (bitrate.toLowerCase()) {
                    case 'low':
                        return VideoQuality.LOW;
                    case 'medium':
                        return VideoQuality.MEDIUM;
                    case 'high':
                        return VideoQuality.HIGH;
                    case 'near_lossless':
                        return VideoQuality.NEAR_LOSSLESS;
                    default:
                        console.warn(`[VideoExport] 未知的码率设置: ${bitrate}，使用默认值 MEDIUM`);
                        return VideoQuality.MEDIUM;
                }
            };

            const quality = bitrateToQuality(options.bitrate || 'medium');
            const config = {
                // codec: VideoCodec.VP9,
                // codec: VideoCodec.H264,
                // codec: VideoCodec.AV1,
                // codec: VideoCodec.MP4_H265,
                // 使用 MP4 H.264 导出 (WebCodecs + mp4-muxer) 以确保帧率准确和高质量
                codec: VideoCodec.MP4_H264,
                quality: quality
            };

            console.log(`[VideoExport] 视频导出配置: 码率=${options.bitrate}, 质量=${quality}`);

            try {
                await exportVideoWithRecordingCamera(
                    this.renderer as any,
                    this.scene as any,
                    recordingCamera,
                    options.duration ? options.duration : 15, // duration（真实时间模式使用）
                    options.fps ? options.fps : 30,
                    options.resolution ? options.resolution : { width: 1920, height: 1080 },
                    this.gaussianThreeJSRenderer,
                    true,
                    config,
                    animationController || undefined, // 使用时间轴控制器（如果选择时间轴模式） - by helen 需要对接页面参数：时间轴控制器
                    undefined,
                    enhancements
                );
                console.log('单视窗视频导出完成', options.resolution);
                this.setExportingVideoActive(false);
                this.restoreEditorHelpers();
                globalConfig.sceneParams.isPlaying = false;
                if (globalConfig.sceneParams.isAnimation) {
                    this.setGaussianAnimationTime(-1); // 如果是开启动画预览，状态重置为-1
                }
                // ✅ 修复：导出完成后，确保场景状态与当前 skyboxEnabled 设置一致
                // 因为导出过程中 RecordingCamera 可能会临时修改场景状态，需要重新应用
                await this.applySkyboxState(this.skyboxEnabled, this.scene);
                modelDialogTools.showLoadingInfoModal(getI18nText('loadingMessage.videoExportComplete'));
                modelDialogTools.closeLoadingInfoModal(2);
            } catch (error) {
                console.error('单视窗视频导出失败:', error);
                const errorMsg = error instanceof Error ? error.message : String(error);
                modelDialogTools.showLoadingInfoModal(getI18nText('loadingMessage.videoExportFailed', { error: errorMsg }));
                throw error; // 重新抛出以便外部处理
            } finally {
                if (cleanupCaptureCanvas) {
                    cleanupCaptureCanvas();
                    cleanupCaptureCanvas = null;
                }
                this.setExportingVideoActive(false, options.resolution ? options.resolution : { width: 1920, height: 1080 });
                this.restoreEditorHelpers();
                globalConfig.sceneParams.isPlaying = false;
                // ✅ 修复：在 finally 块中也确保场景状态正确恢复
                // 即使导出失败，也要确保场景状态与当前 skyboxEnabled 设置一致
                // 注意：这里不 await，因为 finally 块中不应该阻塞
                void this.applySkyboxState(this.skyboxEnabled, this.scene);
                modelDialogTools.closeLoadingInfoModal(2);
            }

        } catch (error) {
            console.error('导出并下载视频失败:', error);
            this.setExportingVideoActive(false, options.resolution ? options.resolution : { width: 1920, height: 1080 });
            globalConfig.sceneParams.isPlaying = false;
            modelDialogTools.showLoadingInfoModal(getI18nText('loadingMessage.videoExportDownloadFailed', { error: String(error) }));
            modelDialogTools.closeLoadingInfoModal(2);
            throw error;
        }
    }

    /**
     * 确保新载入的模型启用 matrixAutoUpdate 并立即同步一次矩阵
     * 部分加载器（GLTF/FBX）默认关闭自动更新，导致拖拽 Gizmo 时位置不刷新
     */
    private enableAutoUpdateForObject(object: THREE.Object3D) {
        object.traverse((child: THREE.Object3D) => {
            if (!child.matrixAutoUpdate) {
                child.matrixAutoUpdate = true;
            }
            child.updateMatrix();
        });
        object.updateMatrixWorld(true);
    }

    /**
     * 统一模型加载方法
     * @param input 文件或文件路径，支持 File、string 或数组
     * @param options 加载选项
     * @returns Promise<LoadResult | LoadResult[]> 返回加载结果
     */
    public async loadModel(
        input: File | string | (File | string)[],
        options: any = {}
    ): Promise<any> {
        if (!loadUnifiedModel) {
            throw new Error("loadUnifiedModel function not available");
        }

        if (!this.renderer || !this.scene) {
            throw new Error("Renderer or scene not available");
        }

        // 准备相机矩阵
        if (this.camera) {
            this.camera.updateMatrixWorld();
            this.camera.updateProjectionMatrix();

            options.cameraMatrix = new Float32Array(this.camera.matrixWorldInverse.elements);
            options.projectionMatrix = new Float32Array(this.camera.projectionMatrix.elements);
        }
        modelDialogTools.showLoadingInfoModal(getI18nText('loadingMessage.startingLoadModel'));

        const isPlyHeatmap = await isPlyHeatmapFile(input);
        const isViewPanelHasHeatmap = this.objects.some(obj => obj.isPlyHeatmap);

        if (isPlyHeatmap && isViewPanelHasHeatmap) { // 已经加载语义模型，同一个scene不能重复加载
            modelDialogTools.showConfirmDialog(getI18nText('heatmap.plyHeatmapFileAlreadyLoadedTitle'), getI18nText('heatmap.plyHeatmapFileAlreadyLoaded', { modelName: input.name }));
            return null;
        }

        try {
            const result = await loadUnifiedModel(
                this.renderer,
                this.scene,
                input,
                options
            );
            console.log("统一模型加载成功:", result);

            // 处理加载结果（可能是单个结果或数组）
            const results = Array.isArray(result) ? result : [result];

            for (const singleResult of results) {
                // 如果返回的是高斯渲染器，尝试重用现有的或保存新的
                if (singleResult.gaussianRenderer) {
                    // 如果已有渲染器，尝试将新模型添加到现有渲染器中
                    if (this.gaussianThreeJSRenderer && singleResult.models.length > 0) {
                        // 重用现有渲染器，将新模型添加到其中
                        const newModel = singleResult.models.find((m: any) => m.isObject3D);
                        if (newModel && this.gaussianThreeJSRenderer.appendGaussianModel) {
                            this.gaussianThreeJSRenderer.appendGaussianModel(newModel);
                            console.log('[VisionaryScene] Added new model to existing GaussianThreeJSRenderer');
                        } else {
                            // 如果没有 appendGaussianModel 方法，使用新的渲染器（会替换旧的）
                            this.gaussianThreeJSRenderer = singleResult.gaussianRenderer;
                        }
                    } else {
                        // 没有现有渲染器，直接使用新的
                        this.gaussianThreeJSRenderer = singleResult.gaussianRenderer;
                    }
                }

                // 处理加载的模型
                if (singleResult.models && singleResult.models.length > 0) {
                    for (const model of singleResult.models) {
                        if (model && model.isObject3D) {
                            const file = singleResult.sourceFile || (input instanceof File ? input : undefined);
                            const url = input instanceof File ? '' : (typeof input === 'string' ? input : '');

                            const forcedId = typeof options?.forcedId === 'string' && options.forcedId.length ? options.forcedId : undefined;
                            const displayName = typeof options?.displayName === 'string' && options.displayName.length ? options.displayName : undefined;

                            if (forcedId) {
                                model.uuid = forcedId;
                            }
                            if (displayName) {
                                model.name = displayName;
                            } else {
                                model.name = file?.name || model.name || url;
                            }

                            this.enableAutoUpdateForObject(model);

                            const name = this.getDisplayName(file?.name || model.name || url);
                            const sourceFile = (file ?? null) as any;
                            const newVisionaryObj = new VisionaryObject(model.uuid, name, 'visionary', model, sourceFile, url);

                            if (isPlyHeatmap) {
                                newVisionaryObj.setIsPlyHeatmap(true);
                            }

                            if (this.gaussianThreeJSRenderer) {
                                const modelIndex = this.findGaussianModelIndexByObject(newVisionaryObj);
                                if (modelIndex >= 0) {
                                    newVisionaryObj.isLoop = true;
                                    this.gaussianThreeJSRenderer.setModelAnimationIsLoop(`model_${modelIndex}`, true);
                                }
                            }
                            this.handleObjectsPush(newVisionaryObj);
                        }
                    }
                }
            }
            if (this.gaussianThreeJSRenderer) {
                console.log('模型加载成功，开启高斯模型动态：', this.gaussianThreeJSRenderer);
                this.gaussianThreeJSRenderer.startAllAnimations(1);
                this.updateRecordingCameraGaussianModels();
                this.applyWeightQueryIfReady();
            }
            modelDialogTools.showLoadingInfoModal(getI18nText('loadingMessage.modelLoadSuccess'));
            return result;
        } catch (error) {
            console.error(getI18nText('errorinfo.loadError'), error);
            // throw error;
            alert(getI18nText('errorinfo.loadError'));
            return null
        } finally {
            if (!options.notCloseLoading) {
                modelDialogTools.closeLoadingInfoModal(0);
            }
        }
    }
    public setNormalModelHide() {
        this.objects.forEach(obj => {
            if (!obj.isPlyHeatmap) {
                (obj.object as any).visible = false;
            }
        });
    }
    // 需要修改 helen
    private applyWeightQueryIfReady(): void {
        const plyModel = globalTools.rightPanelTools?.heatmapPanelTools?.getPlyModelBySceneId(this.id);
        if (!plyModel) return;
        if (!this.gaussianThreeJSRenderer || !plyModel.weightQuery64 || !plyModel.weightGram64x64) return;
        if (typeof (this.gaussianThreeJSRenderer as any).setWeightQueryCosineData === 'function') {
            (this.gaussianThreeJSRenderer as any).setWeightQueryCosineData(plyModel.weightQuery64, plyModel.weightGram64x64);
            return;
        }
        if (typeof this.gaussianThreeJSRenderer.setWeightQueryWeights === 'function') {
            // Backward compatibility (no cosine denominator)
            this.gaussianThreeJSRenderer.setWeightQueryWeights(plyModel.weightQuery64);
        }
    }

    private getDisplayName(displayName: string): string {
        // 生成4个数字字母随机字符串
        const generateRandomString = (): string => {
            const chars = '0123456789abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ';
            let result = '';
            for (let i = 0; i < 4; i++) {
                result += chars.charAt(Math.floor(Math.random() * chars.length));
            }
            return result;
        };

        // 如果 displayName 为空，返回 "未命名_" + 随机字符串
        if (!displayName || displayName.trim() === '') {
            return `未命名_${generateRandomString()}`;
        }

        // 检查是否存在同名对象
        const hasConflict = this.objects.some(obj => obj.name === displayName);

        // 如果不存在冲突，直接返回
        if (!hasConflict) {
            return displayName;
        }

        // 存在冲突，在最后一个 . 之前插入随机字符串
        const lastDotIndex = displayName.lastIndexOf('.');
        if (lastDotIndex === -1) {
            // 没有点号，直接在末尾添加随机字符串
            return `${displayName}_${generateRandomString()}`;
        } else {
            // 在最后一个点号之前插入随机字符串
            const prefix = displayName.substring(0, lastDotIndex);
            const suffix = displayName.substring(lastDotIndex);
            return `${prefix}_${generateRandomString()}${suffix}`;
        }
    }

    private async updateRecordingCameraGaussianModels() {
        if (!this.gaussianThreeJSRenderer) return;
        if (this.activeRecordingPreviewCamera && this.activeRecordingPreviewCamera.isPreviewVisible()) { // 更新录制相机
            console.log("更新录制相机中的高斯模型,重新激活录制相机", this.gaussianThreeJSRenderer);
            await this.activeRecordingPreviewCamera.updateGaussianModels(this.gaussianThreeJSRenderer.getGaussianModels(), this.scene as THREE.Scene);
        }
    }

    handleObjectsPush(visionaryObject: VisionaryObject) {
        console.log('visionaryObject push----', visionaryObject, visionaryObject.object instanceof GaussianModel);
        if (visionaryObject.type === 'visionary') {
            const proxyObject = this.attachProxyForGaussian(visionaryObject.object as GaussianModel);
            visionaryObject.object.pickId = proxyObject.uuid;
        } else {
            visionaryObject.object.pickId = visionaryObject.object.uuid;
            this.selectables.push(visionaryObject.object);
        }
        this.objects.push(visionaryObject);
        if (visionaryObject.isPlyHeatmap) {
            modelDialogTools.showLoadingInfoModal(getI18nText('message.heatmap.plyLoading'));
            window.dispatchEvent(new CustomEvent('event.plyHeatmapLoaded', { detail: { modelId: visionaryObject.id, modelName: visionaryObject.name, sceneId: this.id } }));
            this.setNormalModelHide();
        }
        if (this.objectChangeCallback && typeof this.objectChangeCallback === 'function') {
            this.objectChangeCallback();
        }
        return visionaryObject;
    }

    private handleCanvasClick = (e: MouseEvent): void => {
        if (this.isTransforming) return; // 正在拖拽 Gizmo
        const rect = this.renderer?.domElement?.getBoundingClientRect();

        if (!rect) return;
        this.mouse.x = ((e.clientX - rect.left) / rect.width) * 2 - 1;
        this.mouse.y = -((e.clientY - rect.top) / rect.height) * 2 + 1;
        this.raycaster.setFromCamera(this.mouse, this.camera as any);

        if (this.isPickingPivot) {
            // 优先拾取命中点作为 pivot；否则与 y=0 平面求交
            // const hits = this.raycaster.intersectObjects(this.selectables, true);
            const hits = this.raycaster.intersectObjects(this.selectables.filter(selectable => {
                if (selectable.userData && selectable.userData.target) {
                    return selectable.userData.target.visible;
                }
                return selectable.visible;
            }), true);
            let pivotPoint = new THREE.Vector3();

            if (hits.length && hits[0].point) {
                pivotPoint.copy(hits[0].point);

                // 选中了某个对象
                const hitObject = hits[0].object;
                const selectedObject = this.objects.find((obj) => obj.object.pickId === hitObject.uuid);
                if (selectedObject) {
                    this.setObjectSelected(selectedObject.id, true);
                    globalManager.setCurrentSelected(this.id, selectedObject.id);
                }
            } else {
                // const plane = new THREE.Plane(new THREE.Vector3(0, 1, 0), 0); // y=0
                // const out = new THREE.Vector3();
                // if (this.raycaster.ray.intersectPlane(plane, out)) {
                //     pivotPoint.copy(out);
                // } else {
                //     // 最后兜底：沿射线取固定距离点
                //     const t = 10.0;
                //     pivotPoint.copy(this.raycaster.ray.direction).multiplyScalar(t).add(this.raycaster.ray.origin);
                // }
            }
            // this.gizmoManager?.setPivot(pivotPoint);
            // this.gizmoManager?.setPivotMode('custom' as any);
            // this.updatePivotDisplay(pivotPoint); // 显示pivot点
            return; // 不改变 target
        }
    }

    private onModelSelectChange = (event: Event): void => {
        const detail = (event as CustomEvent).detail;
        if (detail.sceneid != this.id) {
            const selectedObject = globalManager.getCurrentObject();
            if (selectedObject && selectedObject.type === 'recordingCamera') {
                this.syncRecordingCameraByObject(selectedObject);
            } else {
                if (!detail.objectid) { // 旁视窗删除当前对象为录制相机，本视窗关闭录制相机预览 helen 2025-11-26
                    if (this.syncRecordingCamera) {
                        this.syncRecordingCamera.hidePreviewWindow();
                    }
                }
            }
        }
    }

    public syncRecordingCameraByObject = async (object: VisionaryObject): Promise<void> => {
        if (!object || !object.object) return;
        if (object.object.userData && object.object.userData.type === 'recordingCamera') {
            const recordingCamera = object.object.userData.recordingCamera;
            if (!recordingCamera) return;
            let cameraParams = recordingCamera.getParameters();
            if (!this.syncRecordingCamera) {
                // 创建录制相机（复制主相机的状态）
                this.syncRecordingCamera = new RecordingCamera(this.id, cameraParams.width, cameraParams.height, cameraParams.fov, true, object.name, true);
            } else {
                this.syncRecordingCamera.setCameraName(object.name);
            }
            this.syncRecordingCamera.camera.position.copy(recordingCamera.camera?.position);
            this.syncRecordingCamera.camera.quaternion.copy(recordingCamera.camera?.quaternion);
            this.syncRecordingCamera.camera.rotation.copy(recordingCamera.camera?.rotation);
            this.syncRecordingCamera.camera.scale.copy(recordingCamera.camera?.scale);
            this.syncRecordingCamera.camera.near = recordingCamera.camera?.near;
            this.syncRecordingCamera.camera.far = recordingCamera.camera?.far;
            this.syncRecordingCamera.camera.fov = recordingCamera.camera?.fov;
            this.syncRecordingCamera.camera.updateProjectionMatrix();
            this.syncRecordingCamera.camera.updateMatrixWorld();
            this.syncRecordingCamera.syncWrapperTransform();
        }
        if (this.syncRecordingCamera && typeof this.syncRecordingCamera.ensurePreviewWindow === 'function') {
            this.syncRecordingCamera.ensurePreviewWindow(i18next.language as string);
            // 使用场景的公共方法显示预览
            this.syncRecordingCamera.showPreviewWindow(this.skyboxEnabled);
            setTimeout(() => {
                this.showRecordingCameraPreview(this.syncRecordingCamera as RecordingCamera).catch(error => {
                    console.error('[ModelViewPanelTools] 显示录制相机预览失败:', error);
                });
            }, 200);
        }
    }

    // 为 GaussianModel 创建代理（不可见 box，用于被 raycast 命中；作为子物体随变换）
    private attachProxyForGaussian(model: GaussianModel): THREE.Mesh {
        // 基于当前对象包围盒估计
        const box = new THREE.Box3().setFromObject(model as unknown as THREE.Object3D);
        const size = new THREE.Vector3(); const center = new THREE.Vector3();
        box.getSize(size); box.getCenter(center);
        const sx = size.x || 1, sy = size.y || 1, sz = size.z || 1;
        const geom = new THREE.BoxGeometry(sx, sy, sz);
        const mat = new THREE.MeshBasicMaterial({ visible: false });
        // const mat = new THREE.MeshBasicMaterial({ visible: true });
        const proxy = new THREE.Mesh(geom, mat);
        proxy.name = `proxy_${model.name}`;
        proxy.userData.target = model;
        // 放在模型局部空间中心
        proxy.position.copy(center.sub((model as unknown as THREE.Object3D).position));
        (model as unknown as THREE.Object3D).add(proxy);
        this.gaussianProxyMap.set(model, proxy);
        this.selectables.push(proxy);
        return proxy;
    }

    private detachProxyForGaussian(model: GaussianModel): void {
        const proxy = this.gaussianProxyMap.get(model);
        if (!proxy) return;
        const idx = this.selectables.indexOf(proxy);
        if (idx >= 0) this.selectables.splice(idx, 1);
        if (proxy.parent) proxy.parent.remove(proxy);
        this.gaussianProxyMap.delete(model);
    }

    //////////////  Heatmap 相关方法  未完成 Helen 2026-04-17/////////////////////////////////////////
    public setHeatmapPipelineStatus(isActive: boolean): void {
        if (isActive) {
            this.updateHeatmapPipelineById();
        } else {
            try {
                if (this.gaussianThreeJSRenderer?.setWeightOverlayEnabled) {
                    this.gaussianThreeJSRenderer.setWeightOverlayEnabled(false);
                }
            } catch { }
        }
    }

    private updateHeatmapPipelineById(): void {
        const plyModel = globalTools.rightPanelTools?.heatmapPanelTools?.getPlyModelBySceneId(this.id);
        if (plyModel) {
            this.updateHeatmapPipeline(plyModel);
        }
    }

    ////////////////////////  重置、清理、销毁场景相关     /////////////////////////////////////////
    // 重置场景
    public async resetScene(isCloseLoading: boolean = true) {
        modelDialogTools.showLoadingInfoModal(getI18nText('loadingMessage.resettingScene'));
        // 1. 停止动画、关闭录制相机预览窗口
        this.stopAnimation();

        // 2. 清理当前选中
        this.clearCurrentSelected(); // 清空当前选中
        // 4. 清理对象
        this.clearObjects();
        // 5. 清理高斯模型
        this.clearGaussianRenderer();

        // 6. 触发自定义回调
        if (this.objectChangeCallback && typeof this.objectChangeCallback === 'function') {
            this.objectChangeCallback();
        }
        if (this.sceneResetCallback && typeof this.sceneResetCallback === 'function') {
            this.sceneResetCallback();
        }
        this.isLoopAnimation = true;
        this.animate();
        modelDialogTools.showLoadingInfoModal(getI18nText('loadingMessage.sceneResetComplete'));
        if (isCloseLoading) {
            modelDialogTools.closeLoadingInfoModal(1);
        }
    }

    // 4. 清理所有对象
    clearObjects() {
        if (this.scene) {
            this.objects.forEach((obj) => {
                this.scene!.remove(obj.object);
                // 如果有高斯渲染器，清理高斯模型
                if (obj.type === 'visionary' && this.gaussianThreeJSRenderer) {
                    const modelIndex = this.findGaussianModelIndex(obj.id);
                    if (modelIndex >= 0) {
                        try {
                            this.detachProxyForGaussian(obj.object as GaussianModel);
                            // 尝试从高斯渲染器中移除模型
                            if (this.gaussianThreeJSRenderer.removeModelById) {
                                this.gaussianThreeJSRenderer.removeModelById(`model_${modelIndex}`);
                            }
                        } catch (error) {
                            console.warn('移除高斯模型失败:', error);
                        }
                    }
                }
            });
        }
        this.objects = [];
        if (this.gaussianProxyMap) {
            this.gaussianProxyMap.clear();
        }
        if (this.selectables) {
            this.selectables = [];
        }
        // 清除当前选中的对象和gizmo target
        this.currentSelectedObjectId = null;
        this.gizmoManager?.setTarget(null);
    }

    // 6. 清理高斯渲染器  isDestroy: 是否销毁Gizmo管理器,如果只是清理场景，不需要销毁Gizmo管理器
    clearGaussianRenderer(isDestroy: boolean = false) {
        if (this.gizmoManager) {
            try {
                this.gizmoManager.setTarget(null);
            } catch (error) {
                console.warn('清理Gizmo管理器失败:', error);
            }
            if (isDestroy) {
                this.gizmoManager.dispose();
            }
        }

        if (this.gaussianThreeJSRenderer) {
            try {
                if (this.gaussianThreeJSRenderer.dispose) {
                    this.gaussianThreeJSRenderer.dispose();
                }
            } catch (error) {
                console.warn('清理高斯渲染器失败:', error);
            }
            this.gaussianThreeJSRenderer = null;
            console.log('清理高斯渲染器成功');
        }
    }
    /**
     * 销毁场景并释放所有资源
     */
    public destroy(): void {
        if (this.isDestroyed) {
            return;
        }
        this.isDestroyed = true;

        // 1. 停止动画循环
        this.stopAnimation();

        // 2. 清理当前选中
        let currentSelected = globalManager.getCurrentSelected();
        if (currentSelected) {
            globalManager.setCurrentSelected(currentSelected.sceneid, '');
        }
        // 3. 清除所有渲染前/渲染后回调
        this.preRenderCallbacks = [];
        this.postRenderCallbacks = [];
        this.onFPSUpdate = null;

        // 4. 移除窗口大小变化监听器
        window.removeEventListener('resize', this.resizeHandler);
        window.removeEventListener('event.modelSelectChange', this.modelSelectChangeHandler);

        // 6. 清理所有对象
        this.clearObjects();

        // 7. 清理高斯渲染器
        this.clearGaussianRenderer(true);

        // 8. 清理渲染器
        if (this.renderer) {
            try {
                // 移除 DOM 元素
                if (this.renderer.domElement && this.renderer.domElement.parentNode) {
                    this.renderer.domElement.parentNode.removeChild(this.renderer.domElement);
                }
                // WebGPURenderer 可能没有 dispose 方法
                if (typeof (this.renderer as any).dispose === 'function') {
                    (this.renderer as any).dispose();
                }
            } catch (error) {
                console.warn('清理渲染器失败:', error);
            }
            this.renderer = null;
        }

        // 9. 清理场景
        if (this.scene) {
            // 移除场景中的所有对象
            while (this.scene.children.length > 0) {
                this.scene.remove(this.scene.children[0]);
            }
            this.scene = null;
        }

        // 10. 清理相机
        this.camera = null;

        // 11. 清理网格
        this.grid = null;
        this.gridBase = null;

        // 12. 清理回调函数
        this.objectChangeCallback = null;

        // 13. 触发自定义销毁事件
        const customEvent = new CustomEvent('vinSceneDestroy', {
            detail: { sceneid: this.id }
        });
        window.dispatchEvent(customEvent);

        console.log('scene destroyed:', this.id);
    }
}
